import { RubyGemMark } from '../components/RubyGemMark';
import { RubyLoader } from '../components/RubyLoader';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Tag, DollarSign, ArrowLeft, Clock,
  Star, Check, X, AlertCircle, AlertTriangle, Globe, Package, Layers, Send, Percent,
} from 'lucide-react';
import ShimmerImage from '../components/ShimmerImage';
import SearchableDropdown from '../components/SearchableDropdown';
import { DatePicker } from '../components/DatePicker';
import { getIcon } from '../services/iconRegistry';
import { resolveAccent, tint } from '../utils/accentColor';
import DealLandingPreview from '../components/DealLandingPreview';
import SendDealToPartnersModal from '../components/SendDealToPartnersModal';
import { buildDealPreviewModel } from '../components/dealPreviewModel';
import DiscountFields from '../components/finance/DiscountFields';
import DeviceTermFields from '../components/finance/DeviceTermFields';
import OfferTermFields from '../components/finance/OfferTermFields';
import { Note } from '../components/finance/financeUi';
import { money } from '../components/finance/financeFormat';
import { api } from '../services/apiService';
import { dealStrikethrough, resolveOffer, offerAmounts, deviceListPricing, applyDealVersion } from '@platform/shared-types';
// Board 3 — guarded runtime import: frontend CI clones shared-types@development,
// which does not have packageTiers yet, so an unguarded named import of
// mergeOneTierPerFamily/tierLabelOf would fail that build.
import * as SharedTypes from '@platform/shared-types';
const mergeOneTierPerFamily = (SharedTypes as any).mergeOneTierPerFamily
  ?? ((d: string[], s: string[]) => Array.from(new Set([...d, ...s])));
const tierLabelOf = (SharedTypes as any).tierLabelOf ?? ((p: any) => p?.tierLabel);
import {
  billingApi, centsToDollarString, discountToInput, dollarsToCents,
  toDiscount, toDeviceTerm, discountInputError,
  deviceTermToInput, deviceTermError, BLANK_DEVICE_TERM,
  priceIntroToInput, priceIntroError, BLANK_PRICE_INTRO, type PriceIntroInput,
  offerTermToInput, toOfferTerm, BLANK_OFFER_TERM, type OfferTermInput,
  type BillingSkuRow, type DiscountInput, type DeviceTermInput,
} from '../services/billingApi';
import {
  buildDealRequestBody, estateFeeClears, withEstateFeePlan, estateFeeFieldsHiddenNote, dealPackageRows, dealPriceGap, deviceOfferTotals,
  deviceLineRows, deviceDealTotals, dealHeadline, dealScenarios, FINANCE_MODE_LABELS,
  dealPhaseView, deviceShape, dealTotalsFromRows, packagePhasedLines,
  effectiveDealPriceCents, friendlyDealError,
  matchPackagesBlock, serverTotalsProblem, stripePriceNotice, stripePriceSelection,
  swapDealTier,
  type Deal, type DealFormValues, type DealPackagesTotalsView, type PackageItem, type Product,
} from '../services/dealPricing';
import { DealTierPicker, type DealTierOption, DEFAULT_TIER_LABELS } from './deals/DealTierPicker';
import { hardwareApi, type DeviceBundleRow, type DeviceModelRow } from '../services/hardwareApi';
import {
  quoteKit, quoteLabel, fallbackNote, MODE_ICON, MODE_STYLE, type FinancePart,
} from './inventory/bundleFinance';
import {
  DealPaperworkSection, DealDocumentExtras, DealDocumentPreview, useDealDocumentCatalogue,
} from './deals/DealDocumentsSections';
import DealCommissionSection, {
  type CommissionCalcDevice, type CommissionCalcPackage,
} from './deals/DealCommissionSection';
import type { DealDocuments } from '@platform/shared-types';
import DealVersionsPanel from './deals/DealVersionsPanel';
import { DealPublishModal } from './deals/DealPublishModal';
import { dealsApi } from '../services/dealsApi';
import documentsApi from '../services/documentsApi';
import { estateAdminApi, type FeePlan as EstateFeePlan } from '../services/estateAdminApi';

/**
 * DEAL EDIT PAGE — the routed full-page deal editor (`/deals/new`,
 * `/deals/:dealId`).
 *
 * This used to be a two-pane modal living inside `DealsPage.tsx`. It moved to
 * its own route so a deal can be linked to, bookmarked and reloaded directly,
 * without the list underneath it. The form + live preview and every bit of
 * pricing logic are unchanged from the modal — only the shell around them is
 * new. `DealsPage.tsx` keeps the list, delete and active/inactive toggle.
 *
 * TASK 2 — the Stripe price PICKER is gone. Pricing is now
 * packages-list-price → deal `priceCents` → composed subscription, and the
 * backend refusal that used to require a Stripe price for a landing-page deal
 * is retired. A deal that already carries a legacy `stripePriceId` still shows
 * it, read-only, and its amount is still checked against the deal price for a
 * money-safety warning (`stripePriceNotice`) — there is simply no control left
 * here to set or change one.
 */

const ShownToCustomersChip: React.FC<{ label?: string; className?: string }> = ({
  label = 'shown to customers', className = '',
}) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-red-50 text-red-600 border border-red-200 whitespace-nowrap ${className}`}
  >
    👁 {label}
  </span>
);

const InternalOnlyChip: React.FC<{ className?: string }> = ({ className = '' }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-stone-100 text-stone-500 border border-stone-200 whitespace-nowrap ${className}`}
  >
    internal only
  </span>
);

const defaultFormValues: DealFormValues = {
  name: '',
  code: '',
  stripePriceId: '',
  priceDollars: '',
  trialDays: 0,
  // NO FLAT TRIO ON A NEW DEAL (2026-09-03). `percentageRate: 20` used to be
  // seeded here, so every deal created since commission plans shipped carried
  // a dead 20% that nothing pays — see DealFormValues for why that is worse
  // than it sounds. Commission comes from the plan picker below.
  commissionPlanId: '',
  // No Ruby Estate fee plan on a new deal either — same reasoning as
  // commissionPlanId above: the field is simply absent until chosen.
  estateFeePlanId: '',
  showOnLandingPage: false,
  redeemableByCode: false,
  landingPageLabel: '',
  landingPageDescription: '',
  isDefaultDeal: false,
  isActive: true,
  neverExpires: true,
  productId: '',
  packageIds: [],
  packageDiscounts: {},
  packageIntros: {},
  offerTerms: {},
  deviceSkuIds: [],
  deviceTerms: {},
  // Location terms (2026-08-26): required on save; 1 free location is the
  // platform's long-standing default quota, the price must be typed.
  freeLocationQuota: '1',
  additionalLocationPriceDollars: '',
  // A new deal starts with no paperwork; `dealDocumentsConfigured` reads that
  // as "not answered here" and checkout falls back to the device SKU.
  documents: {},
  // The partner's own paperwork — empty means this deal asks a partner to sign
  // nothing, which is what every deal did before this field existed.
  commissionDocuments: { agreementDocumentIds: [], policyDocumentIds: [] },
};

/**
 * One card vocabulary for the whole form — white panels on the page's stone
 * background, the same as every other admin screen.
 */
const LIGHT_PANEL = 'rounded-2xl border border-stone-200 bg-white text-stone-800';
const LIGHT_HEAD = 'font-mono text-[10px] tracking-[.14em] uppercase text-stone-400';
const LIGHT_LABEL = 'flex items-center gap-2 text-[11px] font-medium text-stone-600 mb-1';
const LIGHT_INPUT =
  'w-full px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-lg text-stone-800 '
  + 'placeholder-stone-400 text-sm outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 min-h-[44px]';

/** How one device SKU is paid for, in a phrase. */
const saleModeLabel = (sku: BillingSkuRow): string => {
  const amount = `$${(sku.amountCents / 100).toFixed(2)}`;
  switch (sku.saleMode) {
    case 'outright': return `${amount} one-time`;
    case 'installment': return `${amount}/mo for ${sku.termMonths ?? '?'} months`;
    case 'rental': return `${amount}/mo, no term`;
    case 'lease': return `${amount}/mo lease`;
    case 'included': return 'Included free';
    default: return amount;
  }
};

/**
 * HOW A CUSTOMER PAYS, in the three words they actually use.
 *
 * `saleMode` has five values and a customer recognises three choices: buy it,
 * finance it, rent it. `lease` sits with `installment` because both are "pay
 * monthly until it is yours or the term ends" — the distinction between them is
 * an accounting one, not a decision a buyer makes.
 *
 * Ordered cheapest-commitment-last: buying outright is the plainest thing on the
 * list and belongs at the top, and a device given away is a footnote.
 */
const PLAN_FAMILIES: { key: string; label: string; modes: string[] }[] = [
  { key: 'buy', label: 'Buy', modes: ['outright'] },
  { key: 'finance', label: 'Finance', modes: ['installment', 'lease'] },
  { key: 'rent', label: 'Rent', modes: ['rental'] },
  { key: 'included', label: 'Included', modes: ['included'] },
];

/**
 * A device's payment plans, split into those families.
 *
 * A flat list of six plans — one outright, four terms and a rental — is the
 * thing an operator has to read line by line to find the rental in. Families
 * with no plan produce no heading rather than an empty one, and anything with an
 * unrecognised `saleMode` falls into a trailing "Other" group instead of
 * vanishing from a screen that decides what is sold.
 */
function planFamilies(options: BillingSkuRow[]): { key: string; label: string; options: BillingSkuRow[] }[] {
  const claimed = new Set<string>();
  const groups = PLAN_FAMILIES.map((family) => {
    const matched = (options || []).filter((o) => o.saleMode && family.modes.includes(o.saleMode));
    matched.forEach((o) => claimed.add(o.skuId));
    return { key: family.key, label: family.label, options: matched };
  }).filter((g) => g.options.length > 0);

  const rest = (options || []).filter((o) => !claimed.has(o.skuId));
  return rest.length > 0
    ? [...groups, { key: 'other', label: 'Other', options: rest }]
    : groups;
}

/**
 * Pill switch for the form tiles.
 *
 * The track is a flex ITEM with a fixed width and `shrink-0`, and the knob is
 * positioned inside it — the previous version put an absolutely-positioned knob
 * in a span that could be squeezed by the label beside it, which is how the
 * thumb ended up outside its track.
 */
const TileSwitch: React.FC<{ on: boolean; label: string; onClick: () => void }> = ({ on, label, onClick }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    onClick={onClick}
    className={`inline-flex items-center gap-2 shrink-0 pl-1.5 pr-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${
      on
        ? 'border-rose-300 bg-rose-50 text-rose-700'
        : 'border-stone-300 bg-white text-stone-500 hover:bg-stone-50'
    }`}
  >
    <span
      className={`flex items-center shrink-0 h-4 w-7 rounded-full p-0.5 transition-colors ${
        on ? 'bg-rose-600 justify-end' : 'bg-stone-300 justify-start'
      }`}
    >
      <span className="block h-3 w-3 rounded-full bg-white shadow-sm" />
    </span>
    {label}
  </button>
);

const DealEditPage: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    let off = false;
    import('../services/billingApi')
      .then(({ billingApi }) => billingApi.listCommissionPlans())
      .then((r: any) => { if (!off) setCommissionPlans(r?.items || []); })
      .catch(() => { /* picker shows empty — the deal still saves */ });
    return () => { off = true; };
  }, []);

  useEffect(() => {
    let off = false;
    estateAdminApi.feePlans()
      .then((r) => { if (!off) setEstateFeePlans(r.items || []); })
      .catch(() => { /* picker shows empty — the deal still saves */ });
    return () => { off = true; };
  }, []);
  const { dealId } = useParams<{ dealId: string }>();
  const isNew = !dealId;

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [editingDeal, setEditingDeal] = useState<Deal | null>(null);
  /**
   * The "Send to partners" popup. Only ever reachable for a SAVED deal — see
   * the button in the top bar for why sending an unsaved one would be wrong.
   */
  const [showSendToPartners, setShowSendToPartners] = useState(false);
  const [packages, setPackages] = useState<PackageItem[]>([]);
  /**
   * Commission plans, for the picker in section 3. Loaded independently and
   * best-effort: a plans outage must not stop a deal being priced and saved,
   * and an empty picker reads correctly as "no plan".
   */
  const [commissionPlans, setCommissionPlans] = useState<any[]>([]);
  /**
   * Ruby Estate fee plans (Task 34), for the picker below — loaded
   * best-effort exactly like `commissionPlans` above: an estate-admin outage
   * must not stop a Ruby Till deal (or any deal at all) from saving, and the
   * section that uses this is hidden unless the deal includes an Estate
   * package anyway.
   */
  const [estateFeePlans, setEstateFeePlans] = useState<EstateFeePlan[]>([]);
  /**
   * VERSION ACTIONS, in the top bar beside Save.
   *
   * Save Changes edits the LIVE deal; these two are the versioning pair —
   * Save draft parks a working copy, Publish freezes it as the thing merchants
   * sign against. They sit here because the version panel is at the bottom of
   * a very long form, and an action nobody can find is an action nobody uses.
   */
  const [publishOpen, setPublishOpen] = useState(false);
  /**
   * VERSION STATE LIVES HERE, not in the panel.
   *
   * The banner, the tab badge and the history view must never disagree about
   * what is published — three readers of one fetch, rather than three fetches
   * that can drift apart.
   */
  const [versionTab, setVersionTab] = useState<'deal' | 'versions'>('deal');
  const [versionRows, setVersionRows] = useState<any[]>([]);
  const [activeVersionNo, setActiveVersionNo] = useState<number | null>(null);
  const [versionsImplicit, setVersionsImplicit] = useState(false);
  /**
   * The draft version number this page was SEEDED from, or null when it opened
   * on the published row. Purely so the operator can be told which of the two
   * they are editing — the form itself holds one merged working copy.
   */
  const [editingDraftNo, setEditingDraftNo] = useState<number | null>(null);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState<string | null>(null);

  const [publishChangeType, setPublishChangeType] = useState<'material' | 'minor' | undefined>(undefined);
  const [publishEffectiveAt, setPublishEffectiveAt] = useState(new Date().toISOString());
  const [publishBusy, setPublishBusy] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishChanges, setPublishChanges] = useState<any[]>([]);

  /**
   * PUBLISHING LIVES HERE, and nowhere else.
   *
   * The draft is rewritten from the form first, so what is frozen is what is
   * on screen — the stored draft may be older than the form, and freezing
   * something other than what an operator is looking at is the one mistake
   * this feature exists to prevent.
   */
  const doPublish = async () => {
    if (!editingDeal?.id || !publishChangeType) return;
    setPublishBusy(true);
    setPublishError(null);
    try {
      const patch = await buildVersionPatch();
      await dealsApi.saveDraft(editingDeal.id, patch);
      await dealsApi.publish(editingDeal.id, {
        changeType: publishChangeType,
        effectiveAt: publishEffectiveAt,
      });
      setPublishOpen(false);
      setPublishChangeType(undefined);
      await loadVersions(editingDeal.id);
    } catch (e: any) {
      const pl = e?.payload;
      // A refusal NAMES what is wrong. "Publish failed" tells an operator
      // nothing they can act on.
      setPublishError(
        // Only an UNKNOWN token reaches here now — a field the catalog has
        // never heard of, so nobody can be asked for it either. A field that
        // merely will not resolve is asked of the signer before they sign, and
        // never blocks the publish.
        pl?.error === 'deal_documents_incomplete'
          ? `Unknown merge fields — fix them in the document, or pick from the palette: ${
            (pl.failures || []).map((f: any) => `${f.field} (${f.documentId})`).join(', ')}`
          : pl?.error === 'version_conflict'
            ? 'Somebody else published while you were here. Reload and try again.'
            : e?.message || 'Could not publish',
      );
    } finally {
      setPublishBusy(false);
    }
  };

  /** The draft waiting to be published, if any. */
  const pendingDraft = versionRows.find((v: any) => v.status === 'draft');
  const liveVersion = versionRows.find((v: any) => v.versionNo === activeVersionNo);

  const loadVersions = useCallback(async (id: string) => {
    setVersionsLoading(true);
    setVersionsError(null);
    try {
      const r = await dealsApi.listVersions(id);
      setVersionRows([...r.versions].sort((a, b) => b.versionNo - a.versionNo));
      setActiveVersionNo(r.activeVersionNo);
      setVersionsImplicit(r.implicit);
    } catch (e: any) {
      // Unreachable is not "no history" — those are different facts, and only
      // one of them should worry anybody.
      setVersionsError(e?.message || 'Could not read this deal’s version history');
    } finally {
      setVersionsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (editingDeal?.id) loadVersions(editingDeal.id);
  }, [editingDeal?.id, loadVersions]);
  // Loaded ONCE for all three paperwork panels — see the hook's own comment for
  // why fetching per panel would triple an already N+1 shaped load.
  const documentCatalogue = useDealDocumentCatalogue();
  const [products, setProducts] = useState<Product[]>([]);
  /**
   * Fetched ONLY to check a LEGACY `stripePriceId` for a money-safety conflict
   * (Task 2 retired the picker that used to fill this list for a dropdown).
   */
  const [stripePrices, setStripePrices] = useState<Array<{ id: string; unitAmount: number; interval: string; intervalCount: number }>>([]);
  const [deviceSkus, setDeviceSkus] = useState<BillingSkuRow[]>([]);
  const [bundles, setBundles] = useState<DeviceBundleRow[]>([]);
  const [models, setModels] = useState<DeviceModelRow[]>([]);
  const [hardwareError, setHardwareError] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);
  /** What the last save did, in the operator's words. Cleared on the next edit. */
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  /** Discard is two presses — it deletes work and cannot be undone. */
  const [discardArmed, setDiscardArmed] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  /**
   * Bumped to re-run the load effect against the SAME `dealId` — after a
   * discard, so the form is re-seeded from the server rather than from a second
   * copy of the seeding logic written at the call site.
   */
  const [reloadKey, setReloadKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<DealFormValues>(defaultFormValues);

  /**
   * `packagesTotals` as billing computed it for the deal AS SAVED — kept so
   * the builder can show what the deal totalled before the edit in progress.
   */
  const [savedTotals, setSavedTotals] = useState<DealPackagesTotalsView | null>(null);
  /**
   * Packages the PRODUCT lists that this deal was saved without — added to the
   * product after the deal. Drives the "save to apply" notice; empty for a deal
   * that is already in step with its product.
   */
  const [adoptedFromProduct, setAdoptedFromProduct] = useState<string[]>([]);
  const [productName, setProductName] = useState('');
  /** Board 3 — package families, for the tier picker on each package card. */
  const [families, setFamilies] = useState<{ id: string; tierIds: string[] }[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      setNotFound(false);
      /**
       * EVERYTHING AT ONCE, not in three rounds.
       *
       * The deal, the catalog and the hardware offers do not depend on each
       * other, but this used to await them in sequence: catalog, THEN hardware,
       * THEN the deal. Three round trips of latency stacked up before the form
       * could render, on a page that already loads a lot. They are all fired
       * here and awaited where each is actually needed.
       *
       * The deal's own failure is absorbed into `null` at the call: the await
       * below is no longer the first thing to touch this promise, and an
       * unhandled rejection would surface if the catalog threw first. `null`
       * lands in exactly the same place a thrown error used to — "not found".
       */
      /**
       * A FAILED READ IS NOT A MISSING DEAL.
       *
       * This used to be `.catch(() => null)`, and `null` meant "not found" a
       * few lines down — so an expired token, a 500 or a CORS-stripped 401 all
       * rendered as "Deal not found". An operator was told their deal did not
       * exist when the request had simply been rejected, and the page looked
       * like it would not load.
       *
       * The outcome is carried instead: only a request that SUCCEEDED and
       * returned no deal is a missing deal. Everything else reports why.
       */
      let dealReadError: any = null;
      const dealPromise = dealId
        ? api.deals.get(dealId).catch((e) => { dealReadError = e; return null; })
        : null;

      /**
       * THE UNPUBLISHED DRAFT, LOADED WITH THE DEAL.
       *
       * ⚠️ THE EDITOR MUST OPEN ON THE OPERATOR'S OWN WORK, NOT ON WHAT IS LIVE.
       *
       * `updateDeal` routes EVERY versioned field — `offerTerms`,
       * `packageIntros`, `packageDiscounts`, `priceCents`, `deviceTerms`, the
       * location terms — into a DRAFT VERSION and deliberately does NOT write
       * them to the deal row (`if (drafted.has(field)) continue`). That is
       * correct: the row is what an unpinned tenant still reads, and a price
       * must not move until somebody publishes it.
       *
       * But this page seeded itself from the row ALONE. So an operator saved a
       * discount, was returned to the list, came back in — and every field they
       * had just changed was back to its old value, with a "publish draft"
       * prompt referring to changes they could no longer see anywhere. Their
       * work was never lost; it was simply invisible, which is indistinguishable
       * from lost and much more alarming.
       *
       * So the draft is overlaid onto the row before the form is seeded, through
       * the SAME `applyDealVersion` the pricing path uses — one merge rule, not
       * a second one written here. Best effort: a version service that is down
       * leaves the editor showing the live row, which is what it showed before
       * this existed.
       */
      const draftPromise = dealId
        ? dealsApi.listVersions(dealId)
          .then(async (r) => {
            /*
             * DRAFT FIRST, ELSE THE PUBLISHED VERSION — never the bare row.
             *
             * Overlaying only the DRAFT fixed the save case and left the
             * PUBLISH case broken in exactly the same way: publishing turns the
             * draft into a published version, so there is no draft any more,
             * the overlay found nothing, and the form fell back to the row —
             * which still holds the values the deal was CREATED with. An
             * operator published their work and watched the page revert to the
             * original numbers, which reads as "publishing threw my changes
             * away".
             *
             * The row is not the working copy for ANY commercial field; it is
             * only who the deal is. What the operator should see is the newest
             * commercial truth: their unpublished draft when they have one,
             * otherwise what is currently published.
             */
            const draft = r.versions.find((v) => v.status === 'draft');
            const chosen = draft
              ?? r.versions.find((v) => v.versionNo === r.activeVersionNo && v.status === 'published');
            if (!chosen) return null;
            const full = await dealsApi.getVersion(dealId, chosen.versionNo);
            return {
              versionNo: chosen.versionNo,
              isDraft: chosen.status === 'draft',
              version: full.version as any,
            };
          })
          .catch(() => null)
        : null;

      // Device offers are optional enrichment — a failure here must not stop
      // the editor from loading, but it must be VISIBLE, not silently empty.
      // Not awaited: the form is usable before the device section fills in.
      void Promise.all([
        billingApi.listSkus(),
        hardwareApi.listBundles(),
        hardwareApi.listModels(),
      ]).then(([skuRes, bundleRes, modelRes]) => {
        if (cancelled) return;
        setDeviceSkus((skuRes.items || []).filter(
          (sk) => sk.type === 'device_finance' && sk.status === 'active',
        ));
        setBundles(bundleRes.items || []);
        setModels(modelRes.items || []);
        setHardwareError(null);
      }).catch((e: any) => {
        if (!cancelled) setHardwareError(e?.message || 'Could not load device packages');
      });

      try {
        const [packagesRes, productsRes, pricesRes, familiesRes] = await Promise.all([
          api.packages.list(),
          api.packages.listProducts(),
          api.deals.getStripePrices().catch(() => ({ prices: [] })),
          api.packageFamilies.list().catch(() => ({ families: [] })),
        ]);
        if (cancelled) return;
        setPackages((packagesRes as any)?.packages || []);
        setProducts((productsRes as any)?.products || []);
        setStripePrices((pricesRes as any)?.prices || []);
        setFamilies((familiesRes as any)?.families || []);

        if (dealPromise) {
          try {
            const detail = await dealPromise;
            if (cancelled) return;
            // A rejected read reports why. Only a SUCCESSFUL read that carried
            // no deal is a missing deal.
            if (dealReadError) {
              setLoadError(
                dealReadError?.message
                  || 'The deal could not be read. Your session may have expired — sign in again and retry.',
              );
              return;
            }
            const liveDeal: Deal | undefined = (detail as any)?.deal;
            if (!liveDeal) { setNotFound(true); return; }

            // THE WORKING COPY = the live row with the unpublished draft laid
            // over it. See `draftPromise` above for why the row alone is the
            // wrong thing to seed an EDITOR from.
            const pending = await draftPromise;
            if (cancelled) return;
            const deal = (pending
              ? applyDealVersion(liveDeal as any, pending.version)
              : liveDeal) as Deal;
            // Only a DRAFT gets the "unpublished work" banner. Seeding from the
            // published version is the ordinary state and needs no warning —
            // what is on screen is exactly what customers are being sold.
            setEditingDraftNo(pending?.isDraft ? pending.versionNo : null);

            // `editingDeal` is the row's IDENTITY — what a save is issued
            // against — and deliberately keeps the merged view, so a re-save
            // carries the draft's values forward rather than reverting them to
            // whatever is published.
            setEditingDeal(deal);
            setSavedTotals(liveDeal.packagesTotals || null);

            /**
             * A deal FOLLOWS ITS PRODUCT.
             *
             * `packageIds` is only ever written wholesale from the product (see
             * `changeProduct` — there is no per-package add/remove control), so
             * a deal's stored list is a SNAPSHOT of the product at save time,
             * not a deliberate subset. Packages added to the product afterwards
             * never reached the deal, the portal, or the customer: this deal
             * was saved with 6 while its product had grown to 16.
             *
             * So the list is seeded as the UNION of the product's current
             * defaults and whatever the deal stored. Union, not replace: a
             * package the deal grants but the product no longer lists is still
             * something a live tenant may be paying for, and dropping it
             * silently on open would be worse than showing it.
             *
             * Nothing is written until the operator saves — `adoptedFromProduct`
             * below drives the notice that says what changed and why.
             */
            const product = (productsRes as any)?.products?.find((p: Product) => p.id === deal.productId);
            const catalog = new Set(((packagesRes as any)?.packages || []).map((pk: any) => pk.id));
            // A product can name a package that has since been DELETED from the
            // catalog (rubytill-pro still lists pkg_mmscbex6_oh0ulc). Such an id
            // has no name, no price and nothing to sell — carrying it into a
            // deal shows an operator a bare id and makes the packages
            // untotallable. Dropped here; the product's own list is left alone,
            // because cleaning it is a decision about the product, not this deal.
            const productDefaults: string[] = (Array.isArray(product?.defaultPackages) ? product!.defaultPackages : [])
              .filter((id: string) => catalog.has(id));
            const stored: string[] = (deal.packageIds || []).filter((id: string) => catalog.has(id));
            const fams = ((familiesRes as any)?.families || []) as { id: string; tierIds: string[] }[];
            const packageIds = mergeOneTierPerFamily(productDefaults, stored, fams);
            if (!cancelled) {
              setAdoptedFromProduct(productDefaults.filter((id: string) => packageIds.includes(id) && !stored.includes(id)));
              setProductName(product?.name || deal.productId || '');
            }
            const packageDiscounts: Record<string, DiscountInput> = {};
            for (const id of packageIds) {
              packageDiscounts[id] = discountToInput(deal.packageDiscounts?.[id]);
            }

            setFormData({
              name: deal.name,
              code: deal.code,
              stripePriceId: deal.stripePriceId || '',
              // `priceCents` when the deal has one, else the deprecated
              // `monthlyAmount` — same precedence every reader on the server uses.
              priceDollars: centsToDollarString(effectiveDealPriceCents(deal)),
              trialDays: 0, // retired; see the note by the removed input
              // Round-tripped ONLY when stored, so editing a legacy deal does
              // not blank its old trio and editing a modern one does not
              // invent one. Absent === "this deal has no flat commission".
              ...(deal.commissionMode !== undefined ? { commissionMode: deal.commissionMode } : {}),
              ...(deal.flatFee !== undefined ? { flatFee: (deal.flatFee || 0) / 100 } : {}),
              ...(deal.percentageRate !== undefined ? { percentageRate: deal.percentageRate } : {}),
              commissionPlanId: (deal as any).commissionPlanId || '',
              estateFeePlanId: (deal as any).estateFeePlanId || '',
              ...((deal as any).estateFeeMonths !== undefined ? { estateFeeMonths: (deal as any).estateFeeMonths } : {}),
              showOnLandingPage: deal.showOnLandingPage,
              redeemableByCode: (deal as any).redeemableByCode === true,
              landingPageLabel: deal.landingPageLabel || '',
              landingPageDescription: deal.landingPageDescription || '',
              isDefaultDeal: deal.isDefaultDeal,
              isActive: deal.isActive === 'true',
              maxUses: deal.maxUses,
              expiresAt: deal.expiresAt ? new Date(deal.expiresAt).toISOString().split('T')[0] : undefined,
              neverExpires: !deal.expiresAt,
              productId: deal.productId || '',
              packageIds,
              packageDiscounts,
              // Introductory prices, back into the shape the row edits. Seeded
              // only from what is stored: a package with none opens switched off.
              packageIntros: Object.fromEntries(
                Object.entries((deal as any).packageIntros || {}).map(
                  ([packageId, intro]) => [packageId, priceIntroToInput(intro as any)],
                ),
              ),
              deviceSkuIds: deal.devicePackageIds?.length ? deal.devicePackageIds : (deal.deviceSkuIds || []),
              // Device deal terms, back into the shape the drawer edits. Seeded
              // ONLY from what is stored: a device with no stored term opens
              // blank, and `deviceTermToInput` is the exact inverse of the
              // `toDeviceTerm` that wrote it.
              // THE UNIFIED OFFERS. `resolveOffer` reads the new field where a
              // deal has one and the legacy shapes where it does not, so an
              // old deal opens with the same numbers it was saved with.
              offerTerms: Object.fromEntries(
                [
                  ...(packageIds || []),
                  ...((deal.devicePackageIds?.length ? deal.devicePackageIds : deal.deviceSkuIds) || []),
                ].map((key) => [key, offerTermToInput(resolveOffer({
                  key,
                  offerTerms: (deal as any).offerTerms,
                  packageDiscounts: deal.packageDiscounts,
                  packageIntros: (deal as any).packageIntros,
                  deviceTerms: (deal as any).deviceTerms,
                }))]),
              ),
              deviceTerms: Object.fromEntries(
                Object.entries((deal as any).deviceTerms || {}).map(
                  ([skuId, term]) => [skuId, deviceTermToInput(term as any)],
                ),
              ),
              // Location terms (2026-08-26): a legacy deal without them loads
              // blank and the required-field validation prompts on save —
              // exactly the "existing deals prompt on next edit" rule.
              freeLocationQuota: Number.isInteger((deal as any).freeLocationQuota)
                ? String((deal as any).freeLocationQuota)
                : '',
              additionalLocationPriceDollars: Number.isInteger((deal as any).additionalLocationPriceCents)
                ? centsToDollarString((deal as any).additionalLocationPriceCents)
                : '',
              // The deal's paperwork (2026-08-30), stored in the same shape the
              // form edits — no conversion, unlike every priced field above. A
              // deal saved before this existed opens with an empty binder.
              documents: ((deal as any).documents || {}) as DealDocuments,
              // Same shape stored as edited, like `documents`. A deal saved
              // before this field existed opens with nothing selected.
              commissionDocuments: {
                agreementDocumentIds: (deal as any).commissionDocuments?.agreementDocumentIds || [],
                policyDocumentIds: (deal as any).commissionDocuments?.policyDocumentIds || [],
              },
            });
          } catch (e: any) {
            if (!cancelled) setNotFound(true);
          }
        } else {
          setFormData(defaultFormValues);
        }
      } catch (err: any) {
        if (!cancelled) setLoadError(err?.message || 'Failed to load data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [dealId, reloadKey]);

  // -------------------------------------------------------------------------
  // The packages a deal on a product includes
  // -------------------------------------------------------------------------

  /**
   * The product's packages, MINUS any id with no catalog row.
   *
   * A product can name a package that has since been deleted (rubytill-pro
   * still lists pkg_mmscbex6_oh0ulc). Such an id has no name and no price: it
   * shows an operator a bare id, adds nothing to the total, and cannot be sold.
   * Filtered in ONE place so seeding a deal and switching its product agree —
   * they did not before, and switching product put the dead ids straight back.
   */
  const packagesForProduct = (productId: string): string[] => {
    const product = products.find((p) => p.id === productId);
    const catalog = new Set(packages.map((pk) => pk.id));
    return (Array.isArray(product?.defaultPackages) ? product!.defaultPackages : [])
      .filter((id: string) => catalog.has(id));
  };

  const trimDiscounts = (
    discounts: Record<string, DiscountInput>,
    packageIds: string[],
  ): Record<string, DiscountInput> => {
    const keep = new Set(packageIds);
    return Object.fromEntries(Object.entries(discounts || {}).filter(([id]) => keep.has(id)));
  };

  const changeProduct = (productId: string) => {
    const packageIds = productId ? packagesForProduct(productId) : [];
    setFormData((prev) => ({
      ...prev,
      productId,
      packageIds,
      packageDiscounts: trimDiscounts(prev.packageDiscounts, packageIds),
    }));
  };

  const setPackageDiscount = (packageId: string, patch: Partial<DiscountInput>) => {
    setFormData((prev) => ({
      ...prev,
      packageDiscounts: {
        ...prev.packageDiscounts,
        [packageId]: {
          mode: 'none', value: '', reason: '',
          ...prev.packageDiscounts[packageId],
          ...patch,
        },
      },
    }));
  };

  // -------------------------------------------------------------------------
  // Live figures for the deal being built
  // -------------------------------------------------------------------------

  const packageRows = useMemo(
    () => dealPackageRows(
      formData.packageIds, packages, formData.packageDiscounts,
      formData.packageIntros, formData.offerTerms,
    ),
    [formData.packageIds, formData.packageDiscounts, formData.packageIntros, formData.offerTerms, packages],
  );

  /**
   * The deal's SOFTWARE price over time — "$5/month for 12 months, then $99".
   * Built from the same `buildPricePhases` the server will schedule from, so
   * the sentence an operator reads here is the schedule a customer gets.
   */
  const phaseView = useMemo(() => dealPhaseView(packageRows, money), [packageRows]);

  /**
   * Patch ONE line's offer — package or device, one function, because they are
   * one shape. Seeds a blank offer the first time a line is touched.
   */
  const patchOffer = (key: string, patch: Partial<OfferTermInput>) => setFormData((prev) => ({
    ...prev,
    offerTerms: {
      ...prev.offerTerms,
      [key]: { ...BLANK_OFFER_TERM, ...(prev.offerTerms?.[key] || {}), ...patch },
    },
  }));

  /** Patch one package's introductory price, seeding a blank one on first touch. */
  const patchPackageIntro = (packageId: string, patch: Partial<PriceIntroInput>) => setFormData((prev) => ({
    ...prev,
    packageIntros: {
      ...prev.packageIntros,
      [packageId]: { ...BLANK_PRICE_INTRO, ...(prev.packageIntros?.[packageId] || {}), ...patch },
    },
  }));
  /**
   * SUMMED FROM `packageRows`, not recomputed from the ids.
   *
   * It used to call `liveDealTotals(packageIds, packages, packageDiscounts)` —
   * the retired legacy field, which the unified offer control writes NOTHING
   * to. Every row moved when an operator typed a discount and the total under
   * them did not: "Discount −$0.00", the deal price stuck at list, and — because
   * `priceDollars` is derived from this — that stuck figure was what got SAVED.
   */
  const liveTotals = useMemo(() => dealTotalsFromRows(packageRows), [packageRows]);

  /** The software side as phased lines, for the schedule a scenario markets on. */
  const softwarePhasedLines = useMemo(() => packagePhasedLines(packageRows), [packageRows]);
  const parsedPrice = useMemo(() => dollarsToCents(formData.priceDollars), [formData.priceDollars]);
  const priceCents = parsedPrice.cents ?? null;

  /**
   * The price is DERIVED, so the field the request body is built from is kept
   * in step with the packages rather than typed into. Only when the deal
   * actually has packages: a legacy deal with none keeps whatever price it was
   * stored with instead of being silently zeroed.
   */
  useEffect(() => {
    if (!formData.packageIds.length) return;
    const derived = centsToDollarString(liveTotals.netCents);
    setFormData((prev) => (prev.priceDollars === derived ? prev : { ...prev, priceDollars: derived }));
  }, [liveTotals.netCents, formData.packageIds.length]);

  /**
   * The device catalogue, grouped by the DEVICE rather than by SKU.
   *
   * A terminal offered to rent, finance and buy is three `device_finance`
   * SKUs, and listing them flat made the same hardware appear three times as
   * three unrelated cards. `bundleId`/`modelId` is the device's identity; the
   * SKUs against it are the ways it can be paid for. A SKU with neither falls
   * back to its own id so it still shows up rather than vanishing into a
   * shared bucket.
   *
   * ARCHIVED BUNDLES (`status === 'eol'`) are dropped: an archived kit is no
   * longer offered for sale and must not be addable to a deal. The exception is
   * a SKU THIS DEAL ALREADY HOLDS — an existing deal keeps exactly the hardware
   * it was sold with, so its group still renders and still prices; it simply
   * can never be picked again once removed.
   */
  const deviceGroups = useMemo(() => {
    const archivedBundleIds = new Set(
      bundles.filter((b) => b.status === 'eol').map((b) => b.bundleId),
    );
    const byKey = new Map<string, {
      key: string; title: string; blurb: string; image?: string;
      contents: string[]; cost?: number; options: BillingSkuRow[];
    }>();
    const order: string[] = [];

    for (const sku of deviceSkus) {
      if (
        sku.bundleId
        && archivedBundleIds.has(sku.bundleId)
        && !formData.deviceSkuIds.includes(sku.skuId)
      ) continue;
      const bundle = sku.bundleId ? bundles.find((b) => b.bundleId === sku.bundleId) : undefined;
      const model = sku.modelId ? models.find((m) => m.modelId === sku.modelId) : undefined;
      const key = sku.bundleId || sku.modelId || sku.skuId;

      let group = byKey.get(key);
      if (!group) {
        group = {
          key,
          title: bundle?.name || model?.modelName || sku.name,
          blurb: bundle?.description || model?.description || '',
          image: bundle?.heroImageUrl || bundle?.photos?.[0]
            || model?.media?.heroImageUrl || model?.media?.photos?.[0],
          contents: bundle
            ? bundle.components.map((c) => {
              const m = models.find((x) => x.modelId === c.modelId);
              return `${m?.modelName || c.modelId}${c.quantity > 1 ? ` x${c.quantity}` : ''}`;
            })
            : [],
          cost: bundle?.totalCostCents ?? (model ? model.landedCostCents : undefined),
          options: [],
        };
        byKey.set(key, group);
        order.push(key);
      }
      group.options.push(sku);
    }

    return order.map((k) => byKey.get(k)!);
  }, [deviceSkus, bundles, models, formData.deviceSkuIds]);

  /**
   * KITS, PRICED FROM THEIR PARTS.
   *
   * Selling terms hang off a DEVICE, so a bundle has no SKU of its own and
   * `deviceGroups` — which is built out of SKUs — could never show one. The
   * whole point of building a kit is then lost here: the operator has to
   * remember which four devices were in it and tick them one at a time.
   *
   * A kit is offered as a KIT instead. Picking one adds exactly the SKUs its
   * parts are sold on, which is what the deal stores either way, so nothing
   * downstream has to learn what a bundle is.
   */
  const kitOptions = useMemo(() => {
    const skusByModel = new Map<string, BillingSkuRow[]>();
    for (const s of deviceSkus) {
      if (!s.modelId) continue;
      skusByModel.set(s.modelId, [...(skusByModel.get(s.modelId) || []), s]);
    }
    return bundles
      // An archived kit is no longer sold and must not be addable to a deal.
      .filter((b) => b.status !== 'eol')
      .map((b) => {
        const parts: FinancePart[] = b.components.map((c) => {
          const m = models.find((x) => x.modelId === c.modelId);
          return {
            modelId: c.modelId,
            name: m?.modelName || c.modelId,
            quantity: c.quantity,
            offers: skusByModel.get(c.modelId) || [],
          };
        });
        return {
          bundle: b,
          image: b.heroImageUrl || b.photos?.[0],
          parts,
          quotes: quoteKit(parts),
        };
      })
      // A kit none of whose parts can be sold has nothing to offer a deal.
      .filter((k) => k.quotes.length > 0);
  }, [bundles, models, deviceSkus]);

  /**
   * WHICH KIT THIS DEAL SELLS.
   *
   * Not stored on the deal — a deal holds device SKUs, and adding a field for
   * the kit would give the same fact two homes that could disagree. It is
   * INFERRED instead: the kit whose parts the deal's devices belong to. An
   * existing deal therefore opens on the kit it was built from, without a
   * migration and without anything new to save.
   */
  const [pickedBundleId, setPickedBundleId] = useState<string | null>(null);

  const inferredBundleId = useMemo(() => {
    if (!formData.deviceSkuIds.length) return null;
    const chosenModels = new Set(
      deviceSkus.filter((s) => formData.deviceSkuIds.includes(s.skuId)).map((s) => s.modelId).filter(Boolean),
    );
    if (!chosenModels.size) return null;
    // The kit that best explains what is on the deal: most parts matched, and
    // never a kit none of whose parts are here.
    let best: { id: string; hits: number } | null = null;
    for (const k of kitOptions) {
      const hits = k.parts.filter((p) => chosenModels.has(p.modelId)).length;
      if (hits > 0 && (!best || hits > best.hits)) best = { id: k.bundle.bundleId, hits };
    }
    return best?.id || null;
  }, [formData.deviceSkuIds, deviceSkus, kitOptions]);

  const dealBundleId = pickedBundleId ?? inferredBundleId;
  const selectedKit = kitOptions.find((k) => k.bundle.bundleId === dealBundleId) || null;

  /** Every SKU that belongs to a kit — used to swap one kit out for another. */
  const kitSkuIds = (k: typeof kitOptions[number]) =>
    k.parts.flatMap((p) => p.offers.map((o) => o.skuId));

  /**
   * Choosing a kit REPLACES the previous kit's devices and seeds the new one
   * on its first quote (outright where it exists). Leaving the old kit's
   * devices behind would quietly sell two kits at once.
   */
  const pickBundle = (bundleId: string | null) => {
    const previous = selectedKit;
    const next = bundleId ? kitOptions.find((k) => k.bundle.bundleId === bundleId) || null : null;
    setPickedBundleId(bundleId);
    setFormData((prev: any) => {
      const dropped = previous ? new Set(kitSkuIds(previous)) : new Set<string>();
      const kept = prev.deviceSkuIds.filter((id: string) => !dropped.has(id));
      const seed = next?.quotes.length
        ? (next.quotes.map((q) => q.lines.map((l) => l.skuId).filter(Boolean) as string[])[0] || [])
        : [];
      return { ...prev, deviceSkuIds: [...new Set([...kept, ...seed])] };
    });
  };

  /** Price every device in the kit on one mode, replacing its other offers. */
  const setKitMode = (k: typeof kitOptions[number], skuIds: string[]) => {
    const mine = new Set(kitSkuIds(k));
    setFormData((prev: any) => ({
      ...prev,
      deviceSkuIds: [...new Set([
        ...prev.deviceSkuIds.filter((id: string) => !mine.has(id)),
        ...skuIds,
      ])],
    }));
  };

  /**
   * ONLY THE KIT'S DEVICES ARE OFFERED. Anything already on the deal from
   * outside it is still listed — an existing deal keeps exactly the hardware
   * it was sold with — but nothing new from the wider catalogue is.
   */
  const visibleDeviceGroups = useMemo(() => {
    if (!selectedKit) {
      return deviceGroups.filter((g) => g.options.some((o) => formData.deviceSkuIds.includes(o.skuId)));
    }
    const mine = new Set(selectedKit.parts.map((p) => p.modelId));
    return deviceGroups.filter((g) => mine.has(g.key)
      || g.options.some((o) => formData.deviceSkuIds.includes(o.skuId)));
  }, [deviceGroups, selectedKit, formData.deviceSkuIds]);

  const packagesUnpriced = packageRows.some((r) => r.listCents === null || r.noBasePrice);
  const packagesSumCents = packagesUnpriced ? null : liveTotals.listCents;

  const matchBlock = matchPackagesBlock(packageRows, liveTotals);
  const canMatchPackages = !matchBlock;

  /**
   * A LEGACY read-only fact, not an editable field (Task 2). The deal's
   * amount is still checked against this price for a money-safety warning —
   * there is nothing left in this screen that could set or clear it.
   */
  const stripeSelection = stripePriceSelection(formData.stripePriceId, stripePrices);
  const stripeNotice = formData.stripePriceId
    ? stripePriceNotice(priceCents, stripeSelection)
    : null;

  const selectedDevices = deviceSkus.filter((s) => formData.deviceSkuIds.includes(s.skuId));
  const deviceTotals = deviceOfferTotals(selectedDevices);
  const unknownDeviceIds = formData.deviceSkuIds.filter(
    (id) => !deviceSkus.some((s) => s.skuId === id),
  );

  /**
   * The deal's hardware, priced by its own terms — the SAME `deviceDealTiers`
   * the server bills from, reached through `deviceLineRows`. Every device
   * figure on this page reads off these rows, so the drawer's outcome line, the
   * option row's strikethrough and the preview's totals are one computation
   * rather than three that agree by inspection.
   *
   * Keyed by SKU for the per-row lookups below; `deviceRows` keeps the array
   * for the totals, which must not depend on object key order.
   */
  const deviceRows = useMemo(
    // `offerTerms` is what the drawer writes; `deviceTerms` is the legacy field
    // a deal saved before the unification still carries. Offers win.
    () => deviceLineRows(selectedDevices, formData.deviceTerms, formData.offerTerms),
    // `selectedDevices` is rebuilt every render, so the deps are its inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deviceSkus, formData.deviceSkuIds, formData.deviceTerms, formData.offerTerms],
  );
  const deviceRowBySkuId = useMemo(
    () => Object.fromEntries(deviceRows.map((r) => [r.skuId, r])),
    [deviceRows],
  );
  const deviceDeal = useMemo(() => deviceDealTotals(deviceRows), [deviceRows]);

  /**
   * ONE BREAKDOWN PER WAY THE OFFER CAN BE SOLD.
   *
   * `deviceGroups` is already keyed by the DEVICE (bundleId/modelId), which is
   * exactly the grouping a scenario needs: a customer picks one finance option
   * per device, never all of them.
   */
  const dealScenarioList = useMemo(
    () => dealScenarios({
      groups: deviceGroups.map((g) => ({ key: g.key, title: g.title, options: g.options })),
      selectedSkuIds: formData.deviceSkuIds,
      rowsBySkuId: deviceRowBySkuId,
      softwareMonthlyCents: priceCents ?? 0,
      // The software's OWN schedule, so a card that markets "$5/mo" says so
      // instead of quoting the settled price a customer only reaches later.
      softwareLines: softwarePhasedLines,
      format: money,
    }),
    [deviceGroups, formData.deviceSkuIds, deviceRowBySkuId, priceCents, softwarePhasedLines],
  );

  /** Which device drawers are open. Purely visual — never part of what is saved. */
  const [openDeviceTerm, setOpenDeviceTerm] = useState<string | null>(null);

  /** Patch one device's terms, seeding a blank term the first time it is touched. */
  const patchDeviceTerm = (skuId: string, patch: Partial<DeviceTermInput>) => setFormData((prev) => ({
    ...prev,
    deviceTerms: {
      ...prev.deviceTerms,
      [skuId]: { ...BLANK_DEVICE_TERM, ...(prev.deviceTerms?.[skuId] || {}), ...patch },
    },
  }));

  const previewModel = useMemo(
    () => buildDealPreviewModel(formData, packages, deviceSkus, bundles, models),
    [formData, packages, deviceSkus, bundles, models],
  );

  /** Retired plans are hidden, but one already pinned to this deal is kept
      visible — otherwise the field would silently look empty and clear itself
      on the next save. */
  const commissionPlanOptions = useMemo(() => [
    // NO EMPTY OPTION (2026-08-30). "No plan — use the flat rate" pointed at a
    // fallback that no longer exists on this screen: the flat trio is not
    // authored here any more, so choosing it meant "pay whatever the platform
    // default happens to be, or nothing" — an invisible answer to a question
    // about money. A deal must now name its plan, and `saveBlocked` enforces it.
    ...commissionPlans
      .filter((p) => p.status !== 'retired' || p.planId === formData.commissionPlanId)
      .map((p) => ({
        value: p.planId,
        label: `${p.name}${p.status === 'retired' ? ' (retired)' : ''}`,
        subtitle: `${p.rules?.length || 0} rule${(p.rules?.length || 0) === 1 ? '' : 's'} · v${p.version}${p.isDefault ? ' · platform default' : ''}`,
      })),
  ], [commissionPlans, formData.commissionPlanId]);

  /**
   * Whether the Ruby Estate fee-plan fields show at all (Task 34 brief, step
   * 3): DATA-DRIVEN off the packages this deal actually includes, never a
   * hardcoded package-id list (CLAUDE.md "product line is admin data") — true
   * the moment any package on the deal carries `productLine === 'rubyestate'`.
   */
  const dealIsEstate = useMemo(
    () => formData.packageIds.some((id) => packages.find((pk) => pk.id === id)?.productLine === 'rubyestate'),
    [formData.packageIds, packages],
  );

  const estateFeePlanOptions = useMemo(() => [
    ...estateFeePlans
      .filter((p) => !p.archived || p.planId === formData.estateFeePlanId)
      .map((p) => ({
        value: p.planId,
        label: `${p.name}${p.archived ? ' (archived)' : ''}`,
        subtitle: p.isDefault ? 'platform default' : undefined,
      })),
  ], [estateFeePlans, formData.estateFeePlanId]);

  /**
   * THE COMMISSION CALCULATOR'S INPUTS.
   *
   * Deliberately built from the SAME rows every other figure on this page
   * reads — `packageRows` for software, `deviceRows` for hardware — rather
   * than from the raw catalogs. A partner's commission must be a percentage of
   * the price the operator is actually looking at, deal terms and all; reading
   * list prices here would quote a rate against a number this deal does not
   * charge.
   */
  const commissionCalcPackages = useMemo<CommissionCalcPackage[]>(() => {
    const byId = new Map(packages.map((pk: any) => [pk.id, pk]));
    return packageRows.map((row) => {
      const pk: any = byId.get(row.packageId);
      return {
        packageId: row.packageId,
        name: row.name,
        // The SETTLED price, not the introductory one: a commission projection
        // over a promo month would understate what the partner earns for the
        // other eleven. `netCents` is null when the price is unknown, and the
        // calculator excludes such a line rather than treating it as free.
        netCents: row.netCents,
        icon: pk?.icon || '',
        accent: pk,
      };
    });
  }, [packageRows, packages]);

  const commissionCalcDevices = useMemo<CommissionCalcDevice[]>(() => {
    const skuById = new Map(deviceSkus.map((k) => [k.skuId, k]));
    return deviceRows.map((row) => {
      const sku: any = skuById.get(row.skuId);
      const bundle = sku?.bundleId ? bundles.find((b) => b.bundleId === sku.bundleId) : undefined;
      const model = sku?.modelId ? models.find((m) => m.modelId === sku.modelId) : undefined;
      // Landed cost is only ever passed when it is actually KNOWN — a margin
      // rule with no cost must come back `missing_cost`, never a confident $0.
      const cost = bundle?.totalCostCents ?? model?.landedCostCents;
      return {
        skuId: row.skuId,
        name: row.name,
        deviceId: sku?.bundleId || sku?.modelId,
        saleMode: row.saleMode,
        monthlyCents: row.monthlyCents,
        oneTimeCents: row.oneTimeCents,
        ...(typeof cost === 'number' ? { costCents: cost } : {}),
        // A device whose hero was never set still HAS photos — falling back to
        // the first is why a picture appears at all on most rows.
        imageUrl: bundle?.heroImageUrl || bundle?.photos?.[0]
          || model?.media?.heroImageUrl || model?.media?.photos?.[0],
        deviceName: bundle?.name || model?.modelName,
      };
    });
  }, [deviceRows, deviceSkus, bundles, models]);

  /**
   * THE TWO FIGURES A PAYOUT IS ARGUED FROM, read off a plan's rules.
   *
   * A plan is a list of rules; these pick the ones that answer "what does this
   * plan pay on software" and "what does it pay per device". `undefined` when
   * the plan does not say — never a confident zero, which would freeze a
   * promise of nothing into a signed version.
   */
  const softwareRateOf = (plan: any): number | undefined => {
    const rule = (plan?.rules || []).find((r: any) => r.scope === 'software_package' && r.basis === 'percent');
    return typeof rule?.rate === 'number' ? rule.rate : undefined;
  };
  const hardwareFlatOf = (plan: any): number | undefined => {
    const rule = (plan?.rules || []).find((r: any) => r.scope === 'device_offer' && r.basis === 'flat');
    return typeof rule?.amountCents === 'number' ? rule.amountCents : undefined;
  };

  /**
   * WHAT A VERSION FREEZES, taken from the form as it stands.
   *
   * Documents are PINNED here: the deal names a document, and the draft
   * records the version of it that is published right now. That pin is the
   * whole point — without it a signed merchant silently follows the document
   * wherever it goes next.
   */
  const buildVersionPatch = async () => {
    const docs = formData.documents as any;
    const named: { documentId: string; section: 'software' | 'hardware'; required: boolean }[] = [];
    for (const section of ['hardware', 'software'] as const) {
      for (const documentId of docs?.[section]?.agreementDocumentIds || []) {
        named.push({ documentId, section, required: true });
      }
      for (const documentId of docs?.[section]?.policyDocumentIds || []) {
        named.push({ documentId, section, required: false });
      }
    }

    const documents = [];
    for (const d of named) {
      // The head PUBLISHED version, read at draft time. A document with none
      // is left out rather than pinned to a version nobody may sign.
      try {
        const detail = await documentsApi.getDocument(d.documentId);
        const published = (detail.versions || [])
          .filter((v: any) => v.status === 'published')
          .map((v: any) => v.versionNo);
        if (published.length) documents.push({ ...d, versionNo: Math.max(...published) });
      } catch { /* a document we cannot read cannot be pinned */ }
    }

    const plan = commissionPlans.find((p) => p.planId === formData.commissionPlanId);

    return {
      /**
       * THE PRICE, FROZEN. `parsedPrice` is the same figure the form saves to
       * the deal — the monthly software price this version was published at.
       *
       * Without it a version freezes the packages, the documents and the
       * commission and then reads the MONEY from the live deal row, so the
       * next price edit silently re-prices every merchant who ever signed.
       * deals-service refuses to publish a version that lacks it.
       */
      priceCents: parsedPrice.cents ?? 0,
      /** The phase schedule, when the deal prices in phases rather than flat. */
      pricePhases: (formData as any).pricePhases || [],

      packageIds: formData.packageIds,
      /**
       * CONVERTED, NOT SHIPPED RAW.
       *
       * The form holds a DiscountInput — `{ mode, value, reason }`, as typed —
       * while a stored discount is `{ percentOff }` or `{ amountOffCents }`,
       * exactly one of the two. Sending the form's shape straight into the
       * version made every publish fail validation on discounts that were
       * perfectly valid. `toDiscount` is the same converter the deal's own
       * save path uses, so a version cannot disagree with the deal it froze.
       *
       * A discount the converter rejects is DROPPED rather than frozen: the
       * deal itself refuses to save it too, so a version must never carry one.
       */
      packageDiscounts: Object.fromEntries(
        Object.entries(formData.packageDiscounts || {})
          .filter(([, input]) => !discountInputError(input as any))
          .map(([packageId, input]) => [packageId, toDiscount(input as any)])
          .filter(([, discount]) => !!discount),
      ),
      devicePackageIds: formData.deviceSkuIds,
      /** Same rule for device terms: the stored shape, via the same converter. */
      deviceTerms: Object.fromEntries(
        Object.entries(formData.deviceTerms || {})
          .filter(([, input]) => !deviceTermError(input as any))
          .map(([skuId, input]) => [skuId, toDeviceTerm(input as any)])
          .filter(([, term]) => !!term),
      ),
      freeLocationQuota: Number(formData.freeLocationQuota) || 0,
      // The form holds DOLLARS as typed; a version stores cents, like every
      // other money field in the system.
      additionalLocationPriceCents: Math.round((Number(formData.additionalLocationPriceDollars) || 0) * 100),
      documents,
      // The commission SNAPSHOT, never just an id: editing a plan must not
      // retroactively change what a signed deal promised a partner.
      /**
       * A REAL SNAPSHOT, not an id with a label.
       *
       * The rates travel with the version, so editing a commission plan later
       * cannot change what an already-signed deal promised a partner — which
       * is the entire reason a snapshot exists. Storing only the id would have
       * left commission on pull-latest, the one dimension where money is owed
       * to a third party.
       *
       * Rates are read off the plan's own rules: the catch-all software rate
       * and the flat hardware amount are the two figures a payout is argued
       * from, and both are optional because a plan may set only one.
       */
      commissionPlan: plan
        ? {
          planId: plan.planId,
          name: plan.name || plan.planId,
          ...(typeof softwareRateOf(plan) === 'number' ? { softwareRatePercent: softwareRateOf(plan) } : {}),
          ...(typeof hardwareFlatOf(plan) === 'number' ? { hardwareFlatCents: hardwareFlatOf(plan) } : {}),
        }
        : { planId: 'none', name: 'No commission plan' },
      mergeOverrides: {},
    };
  };

  const saveBlocked = (() => {
    if (!formData.productId) return 'Select the product this deal sells.';
    if (!formData.name.trim()) return 'Give this deal a name.';
    if (parsedPrice.error) return `Deal price: ${parsedPrice.error}`;
    const badRow = packageRows.find((r) => r.error);
    if (badRow) return `Discount on ${badRow.name}: ${badRow.error}`;
    // A deal that would quote one number and charge another through a LEGACY
    // Stripe price. Only an amount mismatch blocks — a cadence mismatch is
    // told, not blocked (see `stripePriceNotice.blocksSave`).
    // An amount conflict still blocks — quoting one number and charging
    // another is a money bug. But the deal price is DERIVED now, so it cannot
    // be typed to match: removing the stale Stripe price is the resolution,
    // and the message says so because the button is right there.
    if (stripeNotice?.blocksSave) return `${stripeNotice.text} Remove the legacy Stripe price to resolve it.`;
    // REQUIRED (2026-08-30). Without a plan a sale falls through to the
    // partner's pin and then the platform default, and if neither exists the
    // partner silently earns nothing on a deal someone sold. Blocking here is
    // the same "existing deals prompt on next edit" rule the location terms
    // already follow: a legacy deal opens with an empty picker and cannot be
    // saved again until it names one.
    if (!formData.commissionPlanId.trim()) return 'Choose the commission plan this deal pays partners on.';
    return null;
  })();

  // -------------------------------------------------------------------------
  // Saving
  // -------------------------------------------------------------------------

  /**
   * DISCARD THE DRAFT and re-open on what is published.
   *
   * A full reload rather than a local reset: the published version is the
   * server's answer, and rebuilding the form from it here would be a second
   * implementation of the seed above — the two would drift, and this one would
   * be the copy nobody tested.
   */
  const handleDiscardDraft = async () => {
    if (!editingDeal?.id) return;
    setDiscarding(true);
    setError(null);
    try {
      const { discardedVersionNo } = await dealsApi.discardDraft(editingDeal.id);
      setSaveNotice(`Draft v${discardedVersionNo} discarded — this deal is back to its published version.`);
      setDiscardArmed(false);
      // Re-seeding from the server is what makes the form show the published
      // values again; `reloadKey` drives the load effect.
      setReloadKey((n) => n + 1);
    } catch (e: any) {
      setError(e?.message || 'The draft could not be discarded.');
    } finally {
      setDiscarding(false);
    }
  };

  const handleSaveDeal = async () => {
    // `packages` so a PERCENTAGE software promotion can be mirrored into the
    // absolute `packageIntros` shape the server stores — see "THE LEGACY MIRROR".
    const { body, error: bodyError } = buildDealRequestBody(formData, packages);
    if (bodyError || !body) {
      setError(bodyError || 'This deal could not be saved.');
      return;
    }

    setSaving(true);
    try {
      const res = editingDeal
        // Final review C-I4: a cleared Estate fee plan / month count is sent as null (REMOVE).
        ? await api.deals.update(editingDeal.id, { ...body, ...estateFeeClears(editingDeal as any, formData) })
        : await api.deals.create(body);

      const saved: Deal | undefined = (res as any)?.deal;

      /**
       * ONE SAVE BUTTON. Saving an existing deal ALSO records what the next
       * version would freeze — the packages, the terms, and each named
       * document pinned to the version published right now.
       *
       * Two buttons that both said "save" was one too many, and the draft was
       * the one nobody pressed: an operator edited a deal, saw it saved, and
       * had no idea a version draft existed at all.
       *
       * BEST EFFORT, DELIBERATELY. The deal itself is already written by the
       * time this runs; a version service that is down must not turn a
       * successful save into an error message.
       */
      let draftFailure: string | null = null;
      if (editingDeal?.id) {
        try {
          const patch = await buildVersionPatch();
          await dealsApi.saveDraft(editingDeal.id, patch);
        } catch (e: any) {
          /**
           * NOT SILENT ANY MORE.
           *
           * This was a `console.warn`, and the operator was still told "Deal
           * updated successfully" and sent back to the list. But publishing
           * freezes the DRAFT, not the deal row — so a draft that failed to
           * record here meant the next publish froze the PREVIOUS draft, and
           * the changes just saved never reached a merchant. The save really
           * did succeed, so this reports what did not, rather than claiming
           * the whole save failed.
           */
          console.warn('[deal] saved, but the version draft could not be recorded', e);
          draftFailure = e?.message || 'the version draft could not be recorded';
        }
      }

      if (draftFailure) {
        setError(
          `The deal was saved, but ${draftFailure}. Publishing now would freeze the previous draft, `
          + 'not these changes — save again before you publish.',
        );
        return;
      }

      const warning = serverTotalsProblem(saved?.packagesTotals);

      /**
       * A SAVE KEEPS THE OPERATOR ON THE DEAL.
       *
       * It used to navigate straight back to the list. On a versioned deal that
       * was actively misleading: the pricing fields had gone into a DRAFT, the
       * list shows what is PUBLISHED, so the operator was thrown out to a screen
       * showing the old price with no sign of what they had just done. Coming
       * back in showed the same old price again (fixed above), and the only clue
       * anything had happened was a publish prompt for changes they could not
       * see.
       *
       * Staying put and reloading the versions is what makes the draft visible:
       * the banner names it, the form is already showing it, and Publish is
       * right there. A NEW deal still navigates — to its own editor, because
       * `/deals/new` has no id to reload.
       */
      if (editingDeal?.id) {
        await loadVersions(editingDeal.id);
        setSaveNotice(
          warning
            ? `Saved, but ${warning}`
            : (draftFailure
              ? 'Saved.'
              : 'Saved as an unpublished draft — publish it to change what customers are sold.'),
        );
        return;
      }

      navigate(`/deals/${saved?.id ?? ''}`, {
        state: {
          dealSuccess: warning ? `The deal was saved, but ${warning}` : 'Deal created successfully',
          dealSuccessTone: warning ? 'warn' : 'success',
        },
      });
    } catch (err: any) {
      setError(friendlyDealError(err?.message));
    } finally {
      setSaving(false);
    }
  };

  const productOptions = products.map((p) => ({
    value: p.id,
    label: p.name,
    subtitle: `${(p.defaultPackages || []).length} package(s)`,
  }));

  const crumbName = isNew ? 'New deal' : (editingDeal?.name || (loading ? 'Loading…' : dealId));

  const TopBar: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <div className="bg-black">
      <div className="px-4 md:px-6 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate('/deals')}
          className="p-2 rounded-lg hover:bg-white/10 transition-colors text-gray-400 hover:text-white shrink-0"
          title="Back to Deals"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 text-sm min-w-0">
          <button onClick={() => navigate('/deals')} className="font-semibold text-white hover:underline shrink-0">
            Deals
          </button>
          <span className="text-gray-600">›</span>
          <span className="text-gray-400 shrink-0">{isNew ? 'New deal' : 'Edit'}</span>
          {!isNew && (
            <>
              <span className="text-gray-600">›</span>
              <span className="text-white font-semibold truncate">{crumbName}</span>
            </>
          )}
        </div>
        {children}
      </div>
      <div style={{ background: 'linear-gradient(90deg, #e8365a, #DC143C, #9F1239)', height: '3px' }} />
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-black -m-3 md:-m-6">
        <TopBar />
        <RubyLoader variant="page" className="bg-stone-100 min-h-[calc(100vh-51px)]" />
      </div>
    );
  }

  if (notFound || loadError) {
    return (
      <div className="min-h-screen bg-black -m-3 md:-m-6">
        <TopBar />
        <div className="bg-stone-100 min-h-[calc(100vh-51px)] flex items-center justify-center">
          <div className="text-center max-w-sm px-6">
            <AlertCircle className="w-10 h-10 text-stone-400 mx-auto mb-3" />
            <p className="text-lg font-semibold text-stone-700">
              {notFound ? 'Deal not found' : 'Could not load this deal'}
            </p>
            <p className="mt-1 text-sm text-stone-500">
              {notFound
                ? 'This deal may have been deleted, or the link is wrong.'
                : loadError}
            </p>
            <button
              onClick={() => navigate('/deals')}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium min-h-[44px]"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Deals
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black -m-3 md:-m-6">
      <TopBar>
        <div className="ml-auto flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => navigate('/deals')}
            className="px-4 py-2 rounded-lg border border-white/10 text-gray-300 hover:text-white hover:bg-white/10 text-sm font-medium transition min-h-[44px]"
          >
            Cancel
          </button>
          {/* SEND TO PARTNERS — before Save on purpose, so the outward-facing
              action that mails people is never the one hit by muscle memory.

              Only for a SAVED deal: the popup shows what a partner will
              receive, and every value it shows comes from the stored record.
              Offering it on unsaved edits would send links for terms that are
              still only on screen. */}
          {editingDeal && (
            <button
              onClick={() => setShowSendToPartners(true)}
              title="Give this deal to partners, or withdraw it"
              className="px-4 py-2 rounded-lg border border-rose-500/55 bg-rose-500/15 text-rose-300 hover:bg-rose-500/25 text-sm font-medium transition min-h-[44px] inline-flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              <span className="hidden sm:inline">Send to partners</span>
            </button>
          )}
          {editingDeal && (
            <button
              onClick={() => setPublishOpen(true)}
              title="Freeze this deal as a version merchants sign against"
              className="px-4 py-2 rounded-lg border border-emerald-500/55 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 text-sm font-medium transition min-h-[44px]"
            >
              Publish version
            </button>
          )}
          <button
            onClick={handleSaveDeal}
            disabled={saving || !!saveBlocked}
            title={saveBlocked || undefined}
            className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
          >
            {saving ? <span className="flex justify-center"><RubyGemMark size={16} drawing color="currentColor" /></span> : (editingDeal ? 'Save Changes' : 'Create Deal')}
          </button>
        </div>
      </TopBar>

      <div className="bg-stone-100 min-h-[calc(100vh-51px)]">
        <div className="max-w-[1400px] mx-auto p-4 md:p-6">
          {saveNotice && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-xl mb-4 flex items-start justify-between gap-3">
              <span className="text-sm">{saveNotice}</span>
              <button onClick={() => setSaveNotice(null)} className="font-medium text-emerald-700 hover:text-emerald-900 shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* WHICH COPY IS ON SCREEN.
              Every pricing field a save touches goes into a DRAFT, so on a deal
              with one the form is showing UNPUBLISHED work — different from what
              customers are being sold right now. Saying so is the difference
              between "my changes vanished" and "my changes are waiting to be
              published". */}
          {editingDraftNo !== null && (
            <div className="bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded-xl mb-4 flex items-start gap-2.5">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <div className="text-sm flex-1 min-w-0">
                <p className="font-semibold">
                  You are editing unpublished draft v{editingDraftNo}.
                </p>
                <p className="text-[13px] mt-0.5">
                  Everything below is your saved work, not what customers are being sold. Prices,
                  packages, hardware and offers only reach customers when this draft is
                  <strong> published</strong> — open the Versions tab to publish it or to see what
                  differs from the live version.
                </p>

                {/* THE WAY BACK. A custom confirm, never `window.confirm` —
                    platform rule, and this one destroys work so it must say
                    exactly what is being destroyed. */}
                {!discardArmed ? (
                  <button
                    type="button"
                    onClick={() => setDiscardArmed(true)}
                    className="mt-2 px-3 py-1.5 rounded-lg border border-amber-300 bg-white text-amber-800 text-[12px] font-semibold hover:bg-amber-50 min-h-[32px]"
                  >
                    Discard these changes
                  </button>
                ) : (
                  <div className="mt-2 rounded-lg border border-amber-300 bg-white p-2.5">
                    <p className="text-[12.5px] text-stone-700">
                      Discard draft v{editingDraftNo} and go back to what is published? Everything
                      you have saved but not published is deleted, and this cannot be undone.
                    </p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <button
                        type="button"
                        disabled={discarding}
                        onClick={handleDiscardDraft}
                        className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-[12px] font-bold hover:bg-red-700 disabled:opacity-50 min-h-[32px]"
                      >
                        {discarding ? 'Discarding…' : 'Yes, discard it'}
                      </button>
                      <button
                        type="button"
                        disabled={discarding}
                        onClick={() => setDiscardArmed(false)}
                        className="px-3 py-1.5 rounded-lg border border-stone-300 bg-white text-stone-700 text-[12px] font-semibold hover:bg-stone-50 min-h-[32px]"
                      >
                        Keep editing
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4 flex items-start justify-between gap-3">
              <span>{error}</span>
              <button onClick={() => setError(null)} className="font-medium text-red-600 hover:text-red-800 shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {saveBlocked && (
            <div className="mb-4">
              <Note tone="warn">
                <span className="flex items-start gap-1.5">
                  <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {saveBlocked}
                </span>
              </Note>
            </div>
          )}

          {/* ── ONE PLACE FOR VERSIONS. The deal and its history are two views
               of one thing, so they are two TABS rather than a panel buried at
               the bottom of the form with its own rival Publish button. */}
          {editingDeal && (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              {([
                { id: 'deal' as const, label: 'Deal' },
                { id: 'versions' as const, label: 'Version history', badge: versionRows.length || undefined },
              ]).map((t) => {
                const on = versionTab === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setVersionTab(t.id)}
                    className={`rounded-full px-4 py-2 text-[12.5px] font-semibold transition ${
                      on ? 'bg-emerald-600 text-white' : 'bg-white text-stone-600 border border-stone-300 hover:bg-stone-50'
                    }`}
                  >
                    {t.label}
                    {t.badge ? <span className={`ml-1.5 ${on ? 'text-emerald-100' : 'text-stone-400'}`}>{t.badge}</span> : null}
                  </button>
                );
              })}
            </div>
          )}

          {/* ── WHERE THIS DEAL STANDS, said in one line.
               Three states, and they are genuinely different: nothing frozen
               yet; frozen and matching; frozen with changes waiting. An
               operator who cannot tell which one they are in will assume the
               flattering one. */}
          {editingDeal && !versionsLoading && !versionsError && (
            versionsImplicit ? (
              <div className="mb-4 rounded-xl border border-stone-200 bg-white p-3 text-[12px] text-stone-600">
                <b className="text-stone-900">Never published.</b> This deal is its own implicit v1 — everyone on it
                signed against whatever it said at the time. Publishing freezes what it says now.
              </div>
            ) : pendingDraft ? (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3">
                <span className="text-[12px] text-amber-900">
                  <b>Unpublished changes.</b> v{pendingDraft.versionNo} is a draft — merchants still sign against
                  {activeVersionNo ? ` v${activeVersionNo}` : ' the deal as it stands'} until you publish it.
                </span>
                <button
                  onClick={() => setPublishOpen(true)}
                  className="rounded-lg px-3 py-1.5 text-[12px] font-bold text-white"
                  style={{ background: '#DC143C' }}
                >
                  Publish v{pendingDraft.versionNo}
                </button>
              </div>
            ) : activeVersionNo ? (
              <div className="mb-4 rounded-xl border border-emerald-300 bg-emerald-50 p-3 text-[12px] text-emerald-900">
                <b>v{activeVersionNo} is the published version</b> — what merchants sign against
                {liveVersion?.effectiveAt ? `, effective ${new Date(liveVersion.effectiveAt).toLocaleDateString()}` : ''}.
                Saving changes writes a new draft; nothing reaches merchants until it is published.
              </div>
            ) : null
          )}

          {editingDeal && versionTab === 'versions' ? (
            <DealVersionsPanel
              dealId={editingDeal.id}
              versions={versionRows}
              activeVersionNo={activeVersionNo}
              implicit={versionsImplicit}
              loading={versionsLoading}
              error={versionsError}
              onReload={() => loadVersions(editingDeal.id)}
              liveDeal={{ priceCents: parsedPrice.cents ?? null, packageIds: formData.packageIds }}
            />
          ) : (
          <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_400px] lg:items-start lg:gap-6">
            {/* Form pane */}
            <div className="space-y-6 text-stone-800">

              {/* ONE TILE: what the deal sells, whether the public sees it,
                  the copy that goes with it, and whether it is live. These were
                  four separate cards scattered down the form; they are the same
                  decision — "who gets offered this, and is it on". */}
              <div className={`${LIGHT_PANEL} p-4 space-y-4`}>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-stone-800">What this deal sells</h3>
                  <span className="text-[11px] text-stone-400">hardware, then the software on it</span>
                </div>
                {/* ── WHAT THIS DEAL SELLS: the kit, then the software on it.
                     TWO DROPDOWNS, SIDE BY SIDE, because they are one decision
                     made in one order — a bundle is the hardware a merchant
                     takes, a product is the software that runs on it. They used
                     to sit in different sections of a very long form, so the
                     pair a deal actually is could not be seen at once. The tree
                     underneath is that pair, resolved: what each choice pulled
                     in, without scrolling to two other places to find out. */}
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
                      <span className={LIGHT_HEAD}>Bundle · hardware</span>
                      <span className="text-[10.5px] text-stone-400">optional</span>
                    </div>
                    <SearchableDropdown
                      options={kitOptions.map((k) => ({
                        value: k.bundle.bundleId,
                        label: k.bundle.name,
                        subtitle: k.parts.map((p) => `${p.name}${p.quantity > 1 ? ` ×${p.quantity}` : ''}`).join(' · '),
                      }))}
                      value={dealBundleId}
                      onChange={(v) => pickBundle(v)}
                      placeholder={kitOptions.length ? '-- Select a Bundle --' : 'No kit has sellable devices yet'}
                      searchPlaceholder="Search bundles…"
                      variant="light"
                    />
                    <p className="mt-1.5 text-[11px] text-stone-500">
                      The devices in it are the hardware on this deal, priced below.
                    </p>
                  </div>
                  <div>
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
                      <span className={LIGHT_HEAD}>Product · software</span>
                      {!formData.productId && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-red-600">
                          <AlertCircle className="w-3 h-3" /> required
                        </span>
                      )}
                    </div>
                    <SearchableDropdown
                      options={productOptions}
                      value={formData.productId || null}
                      onChange={(v) => changeProduct(v || '')}
                      placeholder="-- Select a Product --"
                      searchPlaceholder="Search products…"
                      variant="light"
                    />
                    <p className="mt-1.5 text-[11px] text-stone-500">
                      Signups get this product, and its packages are the ones priced below.
                    </p>
                  </div>
                </div>

                {/* THE TREE — what those two dropdowns actually bound. */}
                {(selectedKit || formData.productId) && (
                  <div className="rounded-xl border border-stone-200 bg-stone-50/70 p-3">
                    {selectedKit && (
                      <div className={formData.productId ? 'mb-3' : ''}>
                        <div className="flex items-center gap-2">
                          <Package className="h-3.5 w-3.5 text-stone-500" />
                          <span className="text-[12.5px] font-bold text-stone-900">{selectedKit.bundle.name}</span>
                          <span className="text-[10.5px] text-stone-400">
                            {selectedKit.parts.length} device{selectedKit.parts.length === 1 ? '' : 's'}
                          </span>
                        </div>
                        <div className="mt-1 space-y-0.5 border-l border-stone-300 pl-3 ml-1.5">
                          {selectedKit.parts.map((part) => {
                            // What this device is actually on the deal for —
                            // read from the SKUs picked, never assumed.
                            const picked = part.offers.filter((o) => formData.deviceSkuIds.includes(o.skuId));
                            return (
                              <div key={part.modelId} className="flex flex-wrap items-baseline gap-x-2 text-[11.5px]">
                                <span className="text-stone-700">
                                  {part.name}{part.quantity > 1 ? ` ×${part.quantity}` : ''}
                                </span>
                                {picked.length === 0 ? (
                                  <span className="text-[10.5px] text-stone-400">not on the deal</span>
                                ) : picked.map((o) => (
                                  <span key={o.skuId} className="font-mono text-[10.5px] text-stone-500">
                                    {saleModeLabel(o)}
                                  </span>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {formData.productId && (
                      <div>
                        <div className="flex items-center gap-2">
                          <Layers className="h-3.5 w-3.5 text-stone-500" />
                          <span className="text-[12.5px] font-bold text-stone-900">{productName || 'Product'}</span>
                          <span className="text-[10.5px] text-stone-400">
                            {formData.packageIds.length} package{formData.packageIds.length === 1 ? '' : 's'}
                          </span>
                        </div>
                        <div className="mt-1 space-y-0.5 border-l border-stone-300 pl-3 ml-1.5">
                          {formData.packageIds.length === 0 ? (
                            <p className="text-[11px] text-stone-400">This product lists no packages.</p>
                          ) : formData.packageIds.map((id: string) => {
                            const pkg = packages.find((pk) => pk.id === id);
                            return (
                              <div key={id} className="text-[11.5px] text-stone-700">
                                {pkg?.name || id}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Visibility + status, on one line */}
                <div className="flex items-center gap-2 flex-wrap pt-3 border-t border-stone-100">
                  <TileSwitch
                    on={formData.showOnLandingPage}
                    label="On landing page"
                    onClick={() => setFormData({ ...formData, showOnLandingPage: !formData.showOnLandingPage })}
                  />
                  {/* Featured means nothing for a deal nobody is shown. */}
                  {formData.showOnLandingPage && (
                    <TileSwitch
                      on={formData.isDefaultDeal}
                      label="Featured"
                      onClick={() => setFormData({ ...formData, isDefaultDeal: !formData.isDefaultDeal })}
                    />
                  )}
                  {editingDeal && (
                    <TileSwitch
                      on={formData.isActive}
                      label={formData.isActive ? 'Active' : 'Inactive'}
                      onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
                    />
                  )}
                  {/*
                    REDEEMABLE BY CODE — the ISO case. Only meaningful for a
                    deal that is NOT listed: a landing-page deal is already
                    applicable by anyone, so offering the switch there would
                    imply a restriction that does not exist.
                  */}
                  {!formData.showOnLandingPage && (
                    <TileSwitch
                      on={formData.redeemableByCode}
                      label="Redeemable by code"
                      onClick={() => setFormData({ ...formData, redeemableByCode: !formData.redeemableByCode })}
                    />
                  )}
                </div>

                {/*
                  Stated in full, because this is the one switch here that gives
                  something away: the code becomes a bearer token, and anyone it
                  is forwarded to can put this offer on their own account.
                */}
                {!formData.showOnLandingPage && formData.redeemableByCode && (
                  <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11.5px] leading-snug text-amber-900">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>
                      Anyone who types <code className="font-mono font-semibold">{formData.code || 'this code'}</code> during
                      signup can put this offer on their own account, even though it is not listed anywhere.
                      Treat the code like a password. It still never appears on the pricing page.
                    </span>
                  </div>
                )}

                {/* The copy that feeds the preview — only when it is shown. */}
                {formData.showOnLandingPage && (
                  <div className="grid gap-3 sm:grid-cols-[12rem_1fr] pt-3 border-t border-stone-100">
                    <input
                      type="text"
                      value={formData.landingPageLabel}
                      onChange={(e) => setFormData({ ...formData, landingPageLabel: e.target.value })}
                      placeholder="Badge — e.g. Best Value"
                      className={LIGHT_INPUT}
                    />
                    <input
                      type="text"
                      value={formData.landingPageDescription}
                      onChange={(e) => setFormData({ ...formData, landingPageDescription: e.target.value })}
                      placeholder="One line about what makes this deal worth taking"
                      className={LIGHT_INPUT}
                    />
                  </div>
                )}
              </div>

              {/* SECTION 2: Deal Info
                  Three named groups inside one panel — what the deal is called,
                  how long it may be taken, and what a location costs on it. The
                  groups are the panel vocabulary used everywhere else on this
                  page (LIGHT_HEAD strip, hairline rule, two responsive columns,
                  a helper line under every field so the rows share a baseline). */}
              <div className={`${LIGHT_PANEL} p-4 space-y-5`}>
                <div>
                  <span className={`${LIGHT_HEAD} flex items-center gap-2`}>
                    <Tag className="w-3 h-3" /> Deal information
                  </span>
                  <p className="mt-1 text-[11.5px] text-stone-500">
                    What this deal is called, how long it can be taken, and the location terms it sells on.
                  </p>
                </div>

                {/* Identity */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className={LIGHT_LABEL}>
                      Deal name <span className="text-rose-500">*</span> <ShownToCustomersChip />
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Partner Special"
                      className={LIGHT_INPUT}
                    />
                    <p className="text-[11px] text-stone-500 mt-1">The name a customer sees on the offer.</p>
                  </div>
                  <div>
                    <label className={LIGHT_LABEL}>
                      Deal code <InternalOnlyChip />
                    </label>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      placeholder="Auto-generated"
                      disabled={!!editingDeal}
                      className={`${LIGHT_INPUT} font-mono disabled:opacity-50`}
                    />
                    <p className="text-[11px] text-stone-500 mt-1">
                      {editingDeal
                        ? 'Fixed once the deal exists — referral links carry it.'
                        : 'Left blank, one is generated on save.'}
                    </p>
                  </div>
                </div>

                {/* Availability
                    A "Trial Days" input stood first in this row. Removed because
                    trials retired 2026-08-28 — the offer is price phases on
                    packages and devices. Stored deals keep whatever number they
                    already carry — nothing reads it, and dropping the field
                    would break the schema check — but no new deal can set one. */}
                <div className="pt-4 border-t border-stone-100 space-y-3">
                  <span className={LIGHT_HEAD}>Availability</span>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      {/* min-h matches the Expires label row, whose TileSwitch
                          is taller than plain label text — without it the two
                          inputs in this row start at different heights. */}
                      <label className={`${LIGHT_LABEL} min-h-[30px]`}>Max uses</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.maxUses || ''}
                        onChange={(e) => setFormData({ ...formData, maxUses: e.target.value ? Number(e.target.value) : undefined })}
                        placeholder="∞"
                        className={LIGHT_INPUT}
                      />
                      <p className="text-[11px] text-stone-500 mt-1">Blank means unlimited signups.</p>
                    </div>
                    <div>
                      {/* The expiry used to be a green "Never ✓" button with a
                          separate text link underneath — two controls in one
                          field, in a colour this page uses nowhere else. It is
                          now the page's own TileSwitch, with the date input
                          disabled while the switch is on so the two columns
                          keep the same height. */}
                      <div className={`${LIGHT_LABEL} justify-between min-h-[30px]`}>
                        <span>Expires</span>
                        <TileSwitch
                          on={formData.neverExpires}
                          label="Never"
                          onClick={() => setFormData({
                            ...formData,
                            neverExpires: !formData.neverExpires,
                            expiresAt: formData.neverExpires ? formData.expiresAt : undefined,
                          })}
                        />
                      </div>
                      {/* The shared DatePicker, not `<input type="date">` —
                          native pickers are banned on this platform and the
                          browser's mm/dd/yyyy chrome matched nothing else on
                          the page. Sized to the LIGHT_INPUT height so it lines
                          up with Max uses beside it. */}
                      <DatePicker
                        value={formData.expiresAt || ''}
                        onChange={(v) => setFormData({ ...formData, expiresAt: v || undefined })}
                        disabled={formData.neverExpires}
                        placeholder="Pick a date"
                        className="!h-[44px] !bg-stone-50 !border-stone-200"
                      />
                      <p className="text-[11px] text-stone-500 mt-1">
                        {formData.neverExpires
                          ? 'This deal stays offered until it is switched off.'
                          : 'Last day the deal can be taken.'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* LOCATION TERMS (2026-08-26 Plan & Packages spec) — REQUIRED.
                    Every deal defines its own location economics: signup seeds
                    tenant.freeLocationQuota from the deal, and additional
                    locations are approved at this price (admin comp/discount
                    applies on top). */}
                <div className="pt-4 border-t border-stone-100 space-y-3">
                  <span className={LIGHT_HEAD}>Location terms</span>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className={LIGHT_LABEL}>
                        Free location quota <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={formData.freeLocationQuota}
                        onChange={(e) => setFormData({ ...formData, freeLocationQuota: e.target.value })}
                        placeholder="e.g., 1"
                        className={LIGHT_INPUT}
                      />
                      <p className="text-[11px] text-stone-500 mt-1">Included free — becomes the tenant's quota at signup.</p>
                    </div>
                    <div>
                      <label className={LIGHT_LABEL}>
                        Additional location price ($/mo) <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={formData.additionalLocationPriceDollars}
                        onChange={(e) => setFormData({ ...formData, additionalLocationPriceDollars: e.target.value })}
                        placeholder="e.g., 39.99"
                        className={LIGHT_INPUT}
                      />
                      <p className="text-[11px] text-stone-500 mt-1">Per location beyond the quota. Comp or discount still applies at approval.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* SECTION 2b: THE BUILDER — packages, discounts, and the price */}
              <div className={`${LIGHT_PANEL} overflow-hidden`}>
                <div className="px-4 py-3 border-b border-stone-200 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-rose-600" />
                  <div>
                    <h3 className="text-sm font-semibold text-stone-800">Software — what this deal costs</h3>
                    <p className="text-[11px] text-stone-500">
                      The product's packages, at list price, a discount on each, and the monthly price this deal offers.
                      Hardware is priced separately, under Bundles below.
                    </p>
                  </div>
                </div>

                <div className="p-4 space-y-3">
                  {!formData.productId ? (
                    <Note tone="info">
                      Select a product above to see the packages this deal includes and what they list at.
                    </Note>
                  ) : packageRows.length === 0 ? (
                    <Note tone="warn">
                      <strong>This product lists no packages.</strong> There is nothing to price against, so the deal's
                      price below stands entirely on its own. Add default packages to the product if that is not
                      intended.
                    </Note>
                  ) : (
                    <div className="space-y-2">
                      {adoptedFromProduct.length > 0 && (
                        <Note tone="info">
                          <strong>
                            {adoptedFromProduct.length} package{adoptedFromProduct.length === 1 ? '' : 's'} added to{' '}
                            {productName || 'this product'} since this deal was last saved
                          </strong>{' '}
                          {adoptedFromProduct.length === 1 ? 'is' : 'are'} included below and priced into the totals.
                          Nothing is stored until you save this deal — partners and customers keep seeing the old set
                          until then.
                        </Note>
                      )}
                      <div className="flex items-center justify-between">
                        <p className={LIGHT_HEAD}>Packages</p>
                        <ShownToCustomersChip label="renders as feature checklist" />
                      </div>
                      {packageRows.map((row) => {
                        // The package as the packages page draws it: its own
                        // colour and icon, same `resolveAccent` maths, so a
                        // package is recognisable wherever it appears.
                        const pkg = packages.find((pk) => pk.id === row.packageId);
                        const a = resolveAccent(pkg ?? {});
                        const PkgIcon = getIcon(pkg?.icon || '');
                        return (
                        <div
                          key={row.packageId}
                          className="relative overflow-hidden rounded-xl border bg-white text-stone-800 p-3"
                          style={{ borderColor: tint(a.from, 0.3) }}
                        >
                          {/*
                            The package's colour entering from the BOTTOM-RIGHT
                            and dissolving before it reaches the middle, so the
                            icon and name always sit on plain white.

                            Full strength at the edge it starts from, gone by
                            the halfway mark.

                            An angled linear-gradient rather than a clip-path:
                            a clipped wedge has an edge, and an edge is a hard
                            stop no matter how it is coloured. Fading the alpha
                            to zero around the 60% mark gives the same angular
                            direction with nothing to catch the eye where it
                            ends, and it never puts saturated colour under the
                            text above it.
                          */}
                          <div
                            aria-hidden
                            className="absolute inset-0 pointer-events-none"
                            style={{
                              background: `linear-gradient(326deg, ${tint(a.from, 0.34)} 0%, ${tint(a.to, 0.14)} 26%, rgba(255,255,255,0) 50%)`,
                            }}
                          />
                          {/* A little more weight in the corner it grows from. */}
                          <div
                            aria-hidden
                            className="absolute inset-0 pointer-events-none"
                            style={{
                              background: `radial-gradient(70% 100% at 100% 100%, ${tint(a.from, 0.22)}, transparent 50%)`,
                            }}
                          />

                          <div className="relative flex items-start justify-between gap-3 flex-wrap">
                            <div className="min-w-[10rem] flex-1 flex items-start gap-2.5">
                              <div
                                className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 shadow-sm"
                                style={{ background: a.band }}
                              >
                                <PkgIcon className="w-4 h-4" style={{ color: a.ink }} />
                              </div>
                              <div className="min-w-0">
                                <span className="font-semibold text-sm text-stone-900">{row.name}</span>
                                <p className="text-[11px] text-stone-500 font-mono mt-0.5 truncate">{row.packageId}</p>
                                {row.listCents === null && (
                                  <p className="text-[11px] text-red-700 mt-0.5">
                                    Not in the packages catalog — contributes nothing.
                                  </p>
                                )}
                                {row.noBasePrice && (
                                  <p className="text-[11px] text-amber-700 mt-0.5">No base price set.</p>
                                )}
                                {(() => {
                                  // Owner 2026-10-11 "even if it's off it should show, always
                                  // all 3": a package with no family passes `options: []`
                                  // straight through — DealTierPicker draws its own three
                                  // default, all-unclickable slots for that case. A package
                                  // IN a family always gets exactly 3 slots, ONE PER RANK
                                  // (1=Basic, 2=Pro, 3=Advanced) rather than one per
                                  // `fam.tierIds` entry — a 2-tier family still shows a third,
                                  // off, Advanced slot. A row's `tierRank` decides which slot
                                  // it fills; a row with no explicit rank falls back to its
                                  // position in `fam.tierIds`. A rank nothing fills renders the
                                  // shared DEFAULT_TIER_LABELS default, greyed and unclickable.
                                  const fam = families.find((f) => f.tierIds.includes(row.packageId));
                                  const options: DealTierOption[] = fam
                                    ? (() => {
                                        const ranked = fam.tierIds.map((id, i) => {
                                          const p = packages.find((pk) => pk.id === id) as any;
                                          const rank = (p && typeof p.tierRank === 'number' && p.tierRank >= 1)
                                            ? p.tierRank
                                            : i + 1;
                                          return { id, p, rank };
                                        });
                                        return DEFAULT_TIER_LABELS.map((defaultLabel, i) => {
                                          const rank = i + 1;
                                          const match = ranked.find((t) => t.rank === rank);
                                          if (match?.p) {
                                            const label = tierLabelOf(match.p) || match.p.name || defaultLabel;
                                            return { id: match.id, label, sell: match.p.status === 'active' };
                                          }
                                          return { id: `__no_tier_rank_${rank}`, label: defaultLabel, sell: false };
                                        });
                                      })()
                                    : [];
                                  return (
                                    <DealTierPicker name={row.name} currentId={row.packageId} options={options}
                                      onPick={(toId) => setFormData((prev) => swapDealTier(prev, row.packageId, toId))} />
                                  );
                                })()}
                              </div>
                            </div>
                            <div className="text-right shrink-0 min-w-[5.5rem]">
                              <p className="text-[10px] uppercase tracking-wider text-stone-500">List</p>
                              <p className="text-sm font-semibold tabular-nums text-stone-700">
                                {row.listCents === null
                                  ? <span className="text-stone-400 font-normal">—</span>
                                  : <>{money(row.listCents)}<span className="text-stone-400 font-normal">/mo</span></>}
                              </p>
                            </div>
                            <div className="text-right shrink-0 min-w-[6rem]">
                              <p className="text-[10px] uppercase tracking-wider text-stone-500">After discount</p>
                              <p className="text-sm font-semibold tabular-nums text-stone-900">
                                {row.netCents === null
                                  ? <span className="text-stone-400 font-normal">—</span>
                                  : <>{money(row.netCents)}<span className="text-stone-400 font-normal">/mo</span></>}
                              </p>
                              {row.listCents !== null && row.netCents !== null && row.netCents !== row.listCents && (
                                <p className="text-[11px] text-amber-700">
                                  −{money(row.listCents - row.netCents)} off
                                </p>
                              )}
                            </div>
                          </div>

                          {/* ONE control for everything this deal changes
                              about this package — the same control a device
                              option uses. It replaced a separate discount row
                              and a separate introductory-price block, which
                              asked the same questions in two places. */}
                          <OfferTermFields
                            className="mt-2.5"
                            value={row.offer}
                            onChange={(patch) => patchOffer(row.packageId, patch)}
                            amounts={row.amounts}
                            hasMonthly
                            hasOneTime={false}
                            hasQuantity={false}
                          />
                        </div>
                        );
                      })}
                    </div>
                  )}

                  {(packageRows.length > 0 || savedTotals) && (
                    <div className="rounded-xl border border-stone-200 bg-stone-50 text-stone-800 p-3">
                      {packageRows.length > 0 && (<>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-stone-500">List</p>
                          <p className="text-lg font-bold tabular-nums text-stone-800">{money(liveTotals.listCents)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-stone-500">Discount</p>
                          <p className="text-lg font-bold tabular-nums text-amber-600">−{money(liveTotals.discountCents)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-stone-500">Packages · monthly</p>
                          <p className="text-lg font-bold tabular-nums text-emerald-700">{money(liveTotals.netCents)}</p>
                        </div>
                      </div>
                      <p className="text-[11px] text-stone-500 mt-2">First location only.</p>
                      {liveTotals.missingPackageIds.length > 0 && (
                        <p className="text-[11px] text-red-700 mt-1">
                          {liveTotals.missingPackageIds.length} package(s) on this deal have no catalog entry
                          ({liveTotals.missingPackageIds.join(', ')}) and contribute nothing above.
                        </p>
                      )}
                      </>)}
                      {savedTotals && serverTotalsProblem(savedTotals) && (
                        <p className="text-[11px] text-amber-700 mt-1">
                          As saved: {serverTotalsProblem(savedTotals)}
                        </p>
                      )}
                      {savedTotals && !serverTotalsProblem(savedTotals) && savedTotals.netCents !== liveTotals.netCents && (
                        <p className="text-[11px] text-stone-600 mt-1">
                          Unsaved — currently stored at{' '}
                          <strong className="tabular-nums">{money(savedTotals.netCents)}</strong>/mo.
                        </p>
                      )}
                    </div>
                  )}

                  {/* THE DEAL'S PRICE — DERIVED, NOT TYPED.
                      One pipeline: package list price, minus each package's own
                      discount, summed. There is no input here by design, so a
                      deal cannot be marked up or undercut away from the parts
                      it is made of. */}
                  <div className="rounded-xl border border-stone-200 bg-stone-50 text-stone-800 p-3">
                    <div className="flex items-end gap-4 flex-wrap">
                      <div>
                        <label className="flex items-center gap-2 flex-wrap text-[10px] uppercase tracking-wider text-stone-500 mb-1">
                          {/* WHICH price is the headline depends on whether there
                              is a limited-time one. Leading with the settled
                              figure while the customer is actually charged a
                              promotional one is the misquote the phase model
                              exists to prevent — so when the schedule changes,
                              the big number is MONTH ONE and the settled price
                              is stated right beside it. */}
                          {phaseView.changes ? 'First month ($ / month)' : 'Deal price ($ / month)'}
                          {' '}<ShownToCustomersChip label="price shown" />
                        </label>
                        <div className="flex items-baseline gap-2">
                          {liveTotals.listCents > (phaseView.changes ? phaseView.firstCents : liveTotals.netCents) && (
                            <span className="text-base text-stone-400 line-through tabular-nums">
                              {money(liveTotals.listCents)}
                            </span>
                          )}
                          <span className="text-3xl font-bold text-stone-900 tabular-nums">
                            {phaseView.changes
                              ? (phaseView.firstCents === 0 ? 'Free' : money(phaseView.firstCents))
                              : money(liveTotals.netCents)}
                          </span>
                          {!(phaseView.changes && phaseView.firstCents === 0) && (
                            <span className="text-xs text-stone-500">/mo</span>
                          )}
                        </div>
                        {phaseView.changes && (
                          <p className="mt-1 flex items-center gap-1.5 flex-wrap text-[11px]">
                            <span className="px-1.5 py-0.5 rounded font-extrabold uppercase tracking-wide text-[9px] bg-rose-100 text-rose-700 border border-rose-200">
                              Limited-time price
                            </span>
                            <span className="text-stone-600">
                              then <strong className="tabular-nums">{money(phaseView.settlesAtCents)}</strong>/mo
                            </span>
                          </p>
                        )}
                      </div>
                      <p className="text-[11px] text-stone-500 flex-1 min-w-[12rem]">
                        Package list prices less their discounts. To change it, change a package's discount.
                        {phaseView.changes && (
                          <>
                            {' '}The deal is stored at its <strong>settled</strong> price of{' '}
                            <span className="tabular-nums">{money(liveTotals.netCents)}</span>/mo — the limited-time
                            price is a schedule on top of it, not a different deal price.
                          </>
                        )}
                      </p>
                    </div>

                    {/* THE SCHEDULE the limited-time prices create, from the SAME
                        phases the subscription is billed on. It used to live only
                        inside the hardware scenarios block, so a software-only
                        deal — the commonest kind — could carry a 12-month promo
                        that no screen ever disclosed. */}
                    {phaseView.changes && (
                      <div className="mt-2.5 rounded-lg border border-rose-200 bg-rose-50/50 p-2.5">
                        <p className="text-[10px] uppercase tracking-[.08em] text-rose-700 font-extrabold mb-1.5">
                          Software price over time
                        </p>
                        {phaseView.phases.map((ph, i) => (
                          <div key={i} className="flex items-baseline gap-2 py-0.5 text-[12px]">
                            <span className="text-stone-600">
                              {ph.iterations
                                ? `Months ${ph.startMonth + 1}–${ph.startMonth + ph.iterations}`
                                : `Month ${ph.startMonth + 1} onward`}
                            </span>
                            <span className="ml-auto font-bold tabular-nums text-stone-900">
                              {ph.totalCents === 0 ? 'Free' : `${money(ph.totalCents)}/mo`}
                            </span>
                          </div>
                        ))}
                        {phaseView.disclosure && (
                          <p className="mt-2 pt-2 border-t border-rose-200 text-[11px] text-stone-700">
                            <span className="font-semibold">Customer is told:</span> &ldquo;{phaseView.disclosure}&rdquo;
                          </p>
                        )}
                        {phaseView.peak && phaseView.peak.cents > phaseView.settlesAtCents && (
                          <p className="mt-1 text-[11px] text-amber-800 flex items-start gap-1.5">
                            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                            Highest software charge is {money(phaseView.peak.cents)}/mo, from month {phaseView.peak.month + 1}.
                          </p>
                        )}
                      </div>
                    )}

                    {packageRows.length === 0 && (
                      <p className="mt-2 text-xs text-amber-800 flex items-center gap-1.5">
                        <AlertTriangle size={13} /> This deal names no packages, so there is nothing to price it
                        from. Add packages to its product first.
                      </p>
                    )}

                  </div>

                  {/* TASK 2 — the Stripe price PICKER is retired. A legacy
                      deal that already carries one still shows it, read-only,
                      and any money-safety conflict is still surfaced. */}
                  {formData.stripePriceId && (
                    <div className="rounded-xl border border-stone-200 bg-stone-50 text-stone-600 p-3 text-xs">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <p>
                          Legacy Stripe price: <code className="font-mono bg-white border border-stone-200 rounded px-1.5 py-0.5">{formData.stripePriceId}</code>
                        </p>
                        {/* The ONLY way out of an amount conflict now that the
                            deal price is derived: the price cannot be typed to
                            match, so the stale Stripe price is what goes. */}
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, stripePriceId: '', clearStripePrice: true })}
                          className="px-3 py-1.5 rounded-lg border border-stone-300 bg-white text-stone-700 text-[11px] font-semibold hover:bg-stone-50"
                        >
                          Remove it
                        </button>
                      </div>
                      {stripeNotice && (
                        <p className={`mt-2 flex items-start gap-1.5 ${stripeNotice.blocksSave ? 'text-red-700' : 'text-amber-800'}`}>
                          <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {stripeNotice.text}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* SECTION 2c: SOFTWARE PAPERWORK (2026-08-30) — placed directly
                  under the software price it applies to, so the two halves of
                  the sale each carry their own documents next to their own
                  money. See `DealDocumentsSections.tsx`. */}
              <DealPaperworkSection
                section="software"
                title="Software paperwork"
                hint="What a tenant signs and accepts for the subscription this deal prices above."
                value={formData.documents}
                onChange={(documents) => setFormData({ ...formData, documents })}
                catalogue={documentCatalogue}
              />

              {/* SECTION 3: Partner commission (2026-08-30).

                  The flat `commissionMode` / `percentageRate` / `flatFee` trio
                  that used to sit here is GONE from the screen. It expressed
                  one rate for the whole deal, could not say "20% on software,
                  $50 a terminal", and was already drawn at 50% opacity under a
                  plan picker that overrode it — two ways to say one thing, one
                  of them dead, both editable.

                  CORRECTED 2026-09-03: an earlier version of this note claimed
                  the trio was "still read by the pay path for deals built
                  before plans existed". IT IS NOT, and was not then.
                  `billing-service/src` contains no reference to any of the
                  three: `pickPlan` walks offer -> partner pin -> deal's plan ->
                  the global default, and records NOTHING when none resolves —
                  it never falls back to a flat rate. The fields are dead
                  storage. The note mattered because it sent a reader looking
                  for a fallback that does not exist.

                  A deal that already carries them keeps them (nothing removes
                  stored data), but a NEW deal no longer gains one — the blank
                  form used to seed `percentageRate: 20`, which is why deals
                  read as paying 20% while their plan paid 2% of margin.

                  What the section carries instead: the plan, the paperwork a
                  partner signs to sell this deal, and what the plan actually
                  pays on every package and device the deal names. */}
              <DealCommissionSection
                planOptions={commissionPlanOptions}
                plans={commissionPlans}
                planId={formData.commissionPlanId}
                onPlanChange={(commissionPlanId) => setFormData({ ...formData, commissionPlanId })}
                onManagePlans={() => navigate('/commission-plans')}
                documents={formData.commissionDocuments}
                onDocumentsChange={(commissionDocuments) => setFormData({ ...formData, commissionDocuments })}
                catalogue={documentCatalogue}
                packages={commissionCalcPackages}
                devices={commissionCalcDevices}
              />

              {/* SECTION 3a: RUBY ESTATE FEE PLAN (Task 34). Shown ONLY when
                  this deal includes a Ruby Estate package — `dealIsEstate`,
                  computed from the catalog's own `productLine`, never a
                  hardcoded package list. Mirrors the commission plan picker
                  above: a plan + an optional "for N months" that defaults to
                  the life of the deal when left blank. */}
              {estateFeeFieldsHiddenNote(dealIsEstate, formData.estateFeePlanId) && (
                <p className="text-xs text-stone-500">{estateFeeFieldsHiddenNote(dealIsEstate, formData.estateFeePlanId)}</p>
              )}
              {dealIsEstate && (
                <div className="p-4 rounded-xl border-2 bg-white text-stone-800" style={{ borderColor: '#DC143C' }}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg" style={{ background: '#fde7ec' }}>
                      <Percent className="w-5 h-5" style={{ color: '#DC143C' }} />
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-stone-800">Ruby Estate fee plan</span>
                      <p className="text-xs text-stone-500">What RubyTill charges this landlord on rent processed, while this deal is in force</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className={LIGHT_LABEL}>Fee plan</label>
                      <SearchableDropdown
                        options={estateFeePlanOptions}
                        value={formData.estateFeePlanId || null}
                        onChange={(estateFeePlanId) => setFormData(withEstateFeePlan(formData, estateFeePlanId))}
                        placeholder="None"
                      />
                    </div>
                    <div>
                      <label className={LIGHT_LABEL}>For N months (blank = life of the deal)</label>
                      <input
                        type="number"
                        min={1}
                        max={60}
                        step={1}
                        value={formData.estateFeeMonths ?? ''}
                        disabled={!formData.estateFeePlanId}
                        onChange={(e) => {
                          const raw = e.target.value;
                          setFormData({ ...formData, estateFeeMonths: raw === '' ? undefined : Number(raw) });
                        }}
                        className={LIGHT_INPUT}
                        placeholder="Life of the deal"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* SECTION 3b: BUNDLES (the hardware side of a deal).
                   THE VOCABULARY IS FIXED: a PRODUCT is software — it is what
                   the packages above hang off — and a BUNDLE is hardware, the
                   kit of devices. This section used to be called "Device
                   Packages", which read as a third kind of package next to the
                   software ones and matched nothing an operator had built. */}
              <div className="p-4 rounded-xl border-2 bg-white text-stone-800" style={{ borderColor: '#DC143C' }}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg" style={{ background: '#fde7ec' }}>
                      <Package className="w-5 h-5" style={{ color: '#DC143C' }} />
                    </div>
                    <div>
                      <span className="inline-flex items-center gap-2 text-sm font-semibold text-stone-800">
                        Bundles <ShownToCustomersChip label="renders with photos" />
                      </span>
                      <p className="text-xs text-stone-500">The hardware kit this deal includes</p>
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-stone-500">
                    {formData.deviceSkuIds.length} device{formData.deviceSkuIds.length === 1 ? '' : 's'} selected
                  </span>
                </div>

                <p className="text-[11px] text-stone-500 mt-2 mb-3">
                  Recorded on the deal, and listed with it on the landing page when it is shown there. Device
                  finance prices through its own SKU and is <strong>not</strong> part of the deal price above, which is
                  the monthly software price. Hardware is not charged at signup yet — that arrives with device
                  fulfilment.
                </p>

                {hardwareError && (
                  <div className="rounded-lg bg-red-50 border border-red-200 p-2.5 text-xs text-red-800 mb-3">
                    {hardwareError} Devices already on this deal are kept and saved unchanged, but until this loads they
                    can only be shown by id, and not totalled.
                  </div>
                )}

                {!hardwareError && deviceSkus.length === 0 && (
                  <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                    <p className="font-semibold">No device has selling terms yet</p>
                    <p className="mt-0.5 text-amber-800">
                      Terms belong to a DEVICE, not to a kit: open a device under Inventory &rarr; Devices and set its
                      selling terms. A bundle then inherits whatever its parts carry, and appears here.
                    </p>
                    <a href="/inventory/stock" className="inline-block mt-1.5 font-semibold underline underline-offset-2">
                      Open Inventory &rarr; Devices &rarr;
                    </a>
                  </div>
                )}

                {/* THE KIT IS CHOSEN AT THE TOP, with the product it runs —
                     one decision, one place. This section is where its devices
                     are priced, not where the kit is picked. */}
                {selectedKit && (
                  <p className="mb-3 text-[11.5px] text-stone-600">
                    Pricing <strong className="text-stone-900">{selectedKit.bundle.name}</strong> — change the kit in
                    “What this deal sells” above.
                  </p>
                )}

                {/* HOW THE WHOLE KIT IS SOLD — one press sets every device in
                    it, and each device can still be changed underneath. */}
                {selectedKit && selectedKit.quotes.length > 0 && (
                  <div className="mb-3 rounded-xl border border-stone-200 bg-stone-50/70 p-2.5">
                    <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-stone-500">
                      Price the whole kit as
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedKit.quotes.map((q) => {
                        const ids = q.lines.map((l) => l.skuId).filter(Boolean) as string[];
                        const on = ids.length > 0 && ids.every((id) => formData.deviceSkuIds.includes(id));
                        const style = MODE_STYLE[q.mode];
                        const Icon = MODE_ICON[q.mode];
                        return (
                          <button
                            key={q.mode}
                            type="button"
                            onClick={() => setKitMode(selectedKit, ids)}
                            title={`Price all ${ids.length} devices ${q.mode}`}
                            className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-left transition ${
                              on ? style.open : style.idle
                            }`}
                          >
                            <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-md ${style.badge}`}>
                              <Icon size={13} />
                            </span>
                            <span>
                              <span className={`block text-[10px] font-bold uppercase tracking-wider ${on ? style.label : 'text-stone-500'}`}>
                                {q.mode}
                              </span>
                              <span className="block font-mono text-[11.5px] font-semibold text-stone-800">
                                {quoteLabel(q, money)}
                              </span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    {(() => {
                      const note = selectedKit.quotes.map(fallbackNote).find(Boolean);
                      return note ? <p className="mt-1.5 text-[10px] text-amber-700">{note}</p> : null;
                    })()}
                  </div>
                )}

                {!dealBundleId && visibleDeviceGroups.length === 0 && kitOptions.length > 0 && (
                  <div className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-[11.5px] text-stone-500">
                    No bundle chosen. Pick one in “What this deal sells” at the top to offer hardware with this deal.
                  </div>
                )}

                {/* ONE ROW PER DEVICE, its finance plans nested underneath.
                    The catalogue holds a SKU per way to pay, so the same
                    terminal appeared three times as three unrelated cards —
                    the operator was picking payment plans while the list read
                    as a hardware list. Grouped by `bundleId`/`modelId`, which
                    is the device's own identity. */}
                <div className="space-y-2">
                  {visibleDeviceGroups.map((group) => {
                    const chosen = group.options.filter((o) => formData.deviceSkuIds.includes(o.skuId));
                    const allOn = chosen.length === group.options.length && group.options.length > 0;
                    const toggleAll = () => setFormData({
                      ...formData,
                      deviceSkuIds: allOn
                        ? formData.deviceSkuIds.filter((id) => !group.options.some((o) => o.skuId === id))
                        : Array.from(new Set([...formData.deviceSkuIds, ...group.options.map((o) => o.skuId)])),
                    });

                    return (
                      <div
                        key={group.key}
                        className={`rounded-xl border-2 overflow-hidden transition ${
                          chosen.length ? 'border-emerald-400 bg-emerald-50/40' : 'border-stone-200 bg-white'
                        }`}
                      >
                        {/* The device */}
                        <div className="flex items-start gap-3 p-3">
                          <ShimmerImage
                            src={group.image}
                            alt={group.title}
                            width={56}
                            height={56}
                            objectFit="contain"
                            className="rounded-lg shrink-0"
                            fallbackBgColor="bg-stone-900"
                            fallbackIcon={<Package size={20} className="text-stone-500" />}
                            variant="dark"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-stone-900 text-sm">{group.title}</span>
                              {chosen.length > 0 && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-600 text-white">
                                  {chosen.length} of {group.options.length}
                                </span>
                              )}
                            </div>
                            {group.blurb && <p className="text-[11px] text-stone-600 mt-0.5 line-clamp-2">{group.blurb}</p>}
                            {group.contents.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {group.contents.map((c) => (
                                  <span key={c} className="px-1.5 py-0.5 rounded text-[10px] bg-white border border-stone-200 text-stone-600">
                                    {c}
                                  </span>
                                ))}
                              </div>
                            )}
                            {typeof group.cost === 'number' && (
                              <div className="text-[10px] text-stone-500 mt-1">costs us ${(group.cost / 100).toFixed(2)}</div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={toggleAll}
                            className="shrink-0 px-2.5 py-1.5 rounded-lg border border-stone-300 bg-white text-stone-600 text-[11px] font-semibold hover:bg-stone-50"
                          >
                            {allOn ? 'None' : 'All'}
                          </button>
                        </div>

                        {/* Its payment plans */}
                        <div className="border-t border-stone-200 bg-stone-50/70 px-3 py-2">
                          <p className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold mb-1.5">
                            Payment plans offered
                          </p>
                          {/* GROUPED BY HOW THE CUSTOMER PAYS. A flat list of
                              six plans is one an operator has to read line by
                              line to find the rental in; three labelled families
                              can be scanned. */}
                          {planFamilies(group.options).map((family) => (
                          <div key={family.key} className="space-y-1 mb-1.5 last:mb-0">
                            <div className="flex items-center gap-2 pt-1">
                              <span className="text-[10px] uppercase tracking-[.1em] font-bold text-stone-500">
                                {family.label}
                              </span>
                              <span className="h-px flex-1 bg-stone-200" />
                              <span className="text-[10px] text-stone-400 tabular-nums">
                                {family.options.filter((o) => formData.deviceSkuIds.includes(o.skuId)).length}
                                /{family.options.length}
                              </span>
                            </div>
                            {family.options.map((opt, i) => {
                              const on = formData.deviceSkuIds.includes(opt.skuId);
                              const isLast = i === family.options.length - 1;
                              const row = deviceRowBySkuId[opt.skuId];
                              const termInput = formData.deviceTerms?.[opt.skuId];
                              const open = openDeviceTerm === opt.skuId;
                              const termProblem = deviceTermError(termInput);
                              // A device is only "priced by the deal" when its
                              // terms actually move a number — a drawer opened
                              // and closed must not badge the row.
                              const priced = Boolean(row?.fromDeal);
                              const isRecurring = opt.saleMode !== 'outright' && opt.saleMode !== 'included';
                              return (
                                <React.Fragment key={opt.skuId}>
                                  <div className="w-full flex items-center gap-2 py-1">
                                    <button
                                      type="button"
                                      onClick={() => setFormData({
                                        ...formData,
                                        deviceSkuIds: on
                                          ? formData.deviceSkuIds.filter((x) => x !== opt.skuId)
                                          : [...formData.deviceSkuIds, opt.skuId],
                                      })}
                                      className="flex items-center gap-2 text-left group min-w-0 flex-1"
                                    >
                                      {/* The tree elbow — └ on the last child, ├ above it. */}
                                      <span className="font-mono text-stone-300 text-xs select-none leading-none">
                                        {isLast ? '└─' : '├─'}
                                      </span>
                                      <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                                        on ? 'bg-emerald-600 border-emerald-600' : 'border-stone-300 bg-white group-hover:border-stone-400'
                                      }`}>
                                        {on && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                                      </span>
                                      <span className={`text-xs truncate ${on ? 'text-stone-800 font-medium' : 'text-stone-600'}`}>
                                        {saleModeLabel(opt)}
                                      </span>
                                    </button>

                                    {/* What the deal actually charges for this
                                        option, readable WITHOUT opening the
                                        drawer — the strikethrough is the whole
                                        point of a discount an operator is
                                        negotiating against a list price. */}
                                    {on && priced && row && (
                                      <span className="flex items-center gap-1.5 shrink-0 tabular-nums text-xs">
                                        {isRecurring ? (
                                          <>
                                            {row.listMonthlyCents > row.monthlyCents && (
                                              <span className="text-stone-400 line-through">{money(row.listMonthlyCents)}</span>
                                            )}
                                            <span className="font-bold text-emerald-700">{money(row.monthlyCents)}/mo</span>
                                          </>
                                        ) : (
                                          <>
                                            {row.listOneTimeCents > row.oneTimeCents && (
                                              <span className="text-stone-400 line-through">{money(row.listOneTimeCents)}</span>
                                            )}
                                            <span className="font-bold text-emerald-700">{money(row.oneTimeCents)}</span>
                                          </>
                                        )}
                                      </span>
                                    )}
                                    {on && priced && termInput?.covers === 'first_n' && (
                                      <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                                        first {termInput.coveredUnits || '?'}
                                      </span>
                                    )}
                                    {on && priced && !row?.showOneTime && (
                                      <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-stone-100 text-stone-600 border border-stone-200">
                                        due-today hidden
                                      </span>
                                    )}
                                    {on && termProblem && (
                                      <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                                        check terms
                                      </span>
                                    )}

                                    {/* Terms are only meaningful for a device
                                        the deal actually includes, so the
                                        control appears with the selection. */}
                                    {on ? (
                                      <button
                                        type="button"
                                        onClick={() => setOpenDeviceTerm(open ? null : opt.skuId)}
                                        className={`shrink-0 px-2.5 py-1 rounded-lg text-[11px] font-bold border transition min-h-[32px] ${
                                          priced
                                            ? 'bg-rose-700 text-white border-rose-700 hover:bg-rose-800'
                                            : 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100'
                                        }`}
                                      >
                                        {open ? 'Done' : priced ? 'Manage ✓' : 'Manage'}
                                      </button>
                                    ) : null}
                                    {/* NOTHING FILLS THE GAP where Manage sits
                                        on an unselected row. It used to carry
                                        the raw SKU id — a truncated
                                        `dev_mdl_cash_drawer_fi…` against every
                                        unticked plan, which named nothing an
                                        operator recognises and made a tidy list
                                        of prices look like a database dump. */}
                                  </div>

                                  {on && open && (() => {
                                    // THE SAME control the package rows use.
                                    // A device answers one extra question
                                    // (how many units) and may take money up
                                    // front; both are shape, not a different
                                    // model, so the component takes them as
                                    // flags rather than existing twice.
                                    const shape = deviceShape(opt.saleMode);
                                    const offer = formData.offerTerms?.[opt.skuId] || BLANK_OFFER_TERM;
                                    return (
                                      <div className="-mx-3 border-t border-dashed border-rose-200 bg-rose-50/40 px-3 py-3">
                                        <h5 className="text-[10px] uppercase tracking-[.09em] text-rose-700 font-extrabold mb-3">
                                          Offer · {saleModeLabel(opt)}
                                        </h5>
                                        <OfferTermFields
                                          value={offer}
                                          onChange={(patch) => patchOffer(opt.skuId, patch)}
                                          amounts={offerAmounts(
                                            deviceListPricing({
                                              skuId: opt.skuId,
                                              amountCents: opt.amountCents,
                                              saleMode: opt.saleMode,
                                              ...(typeof (opt as any).downPaymentCents === 'number'
                                                ? { downPaymentCents: (opt as any).downPaymentCents }
                                                : {}),
                                            }),
                                            toOfferTerm(offer, shape),
                                          )}
                                          hasMonthly={shape.hasMonthly}
                                          hasOneTime={shape.hasOneTime}
                                          hasQuantity={shape.hasQuantity}
                                          termMonths={opt.termMonths}
                                        />
                                      </div>
                                    );
                                  })()}
                                </React.Fragment>
                              );
                            })}
                          </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {unknownDeviceIds.length > 0 && (
                  <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-900">
                    <p className="font-semibold">
                      {unknownDeviceIds.length} device offer(s) on this deal are not in the billing catalog
                      {hardwareError ? ' right now' : ' any more'}.
                    </p>
                    <p className="mt-0.5">
                      They stay on the deal and are counted in no total here.
                    </p>
                    <ul className="mt-1 space-y-1">
                      {unknownDeviceIds.map((id) => (
                        <li key={id} className="flex items-center justify-between gap-2">
                          <span className="font-mono">{id}</span>
                          <button
                            type="button"
                            onClick={() => setFormData({
                              ...formData,
                              deviceSkuIds: formData.deviceSkuIds.filter((x) => x !== id),
                            })}
                            className="px-3 py-2 rounded-lg border border-amber-300 bg-white text-amber-800 font-semibold min-h-[44px]"
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* THE WHOLE OFFER — ONE BREAKDOWN PER WAY IT CAN BE SOLD.
                    A device offered outright, financed and rented is three SKUs
                    and a customer takes ONE, so the old single "devices
                    monthly" total added the same terminal up three times. See
                    `dealScenarios` for why this is the only framing an operator
                    can check a marketed price against. */}
                {formData.deviceSkuIds.length > 0 && !hardwareError && (() => {
                  if (dealScenarioList.length === 0) return null;
                  return (
                    <div className="mt-3 rounded-xl border border-stone-200 bg-white text-stone-800 p-3.5">
                      <div className="flex items-center justify-between mb-2.5">
                        <p className="font-mono text-[10px] tracking-[.12em] uppercase text-stone-400 font-bold">
                          What this offer markets as
                        </p>
                        <span className="text-[10px] text-stone-400">per location · one of each device</span>
                      </div>

                      {/* SOFTWARE — the same in every scenario, so it is stated
                          once above them rather than repeated in each. */}
                      <p className="text-[10px] uppercase tracking-[.08em] text-stone-400 font-bold border-b border-stone-100 pb-1 mb-1">
                        Software — every option
                      </p>
                      {packageRows.length === 0 ? (
                        <p className="text-xs text-stone-400 py-1">No packages on this deal yet.</p>
                      ) : packageRows.map((row) => (
                        <div key={row.packageId} className="flex items-baseline gap-2 py-1 text-[13px]">
                          <span className="text-stone-800 truncate">{row.name}</span>
                          {row.listCents > row.netCents && (
                            <span className="text-[11px] text-stone-400 line-through tabular-nums">{money(row.listCents)}</span>
                          )}
                          <span className="ml-auto font-semibold tabular-nums">{money(row.netCents)}/mo</span>
                        </div>
                      ))}
                      <div className="flex items-baseline gap-2 py-1 text-[12px] border-t border-stone-100 mt-1 pt-1.5 text-stone-500">
                        <span>Software subtotal{phaseView.changes && ' (settled)'}</span>
                        <span className="ml-auto font-semibold tabular-nums">
                          {priceCents === null ? '—' : money(priceCents)}/mo
                        </span>
                      </div>

                      {/* The software-only schedule is NOT repeated here — it is
                          drawn under the deal price above, where a deal with no
                          hardware can also see it. What each scenario card shows
                          instead is the COMBINED schedule for that option, which
                          is the one a customer taking it is actually billed. */}

                      {/* ONE CARD PER FINANCE MODE. These are ALTERNATIVES, not
                          additions — the divider wording says so, because two
                          stacked totals otherwise read as things to sum. */}
                      <div className="mt-3 space-y-2.5">
                        {dealScenarioList.map((sc, scIndex) => (
                          <React.Fragment key={sc.mode}>
                            {scIndex > 0 && (
                              <div className="flex items-center gap-2 py-0.5">
                                <span className="h-px flex-1 bg-stone-200" />
                                <span className="text-[10px] uppercase tracking-[.1em] text-stone-400 font-bold">or</span>
                                <span className="h-px flex-1 bg-stone-200" />
                              </div>
                            )}
                            <div className="rounded-lg border border-stone-200 bg-stone-50/70 p-2.5">
                              <div className="flex items-center gap-2 mb-1.5">
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wide bg-rose-700 text-white">
                                  {sc.label}
                                </span>
                                {sc.rows.some((r) => r.termMonths) && (
                                  <span className="text-[10px] text-stone-500">
                                    {sc.rows.find((r) => r.termMonths)?.termMonths} months
                                  </span>
                                )}
                              </div>

                              {sc.rows.map((r) => (
                                <div key={r.skuId} className="flex items-baseline gap-2 py-0.5 text-[12px]">
                                  <span className="text-stone-700 truncate">{r.deviceName}</span>
                                  {/* A device with no option in THIS mode is
                                      shown at the only price it has — an
                                      operator checking the rental headline must
                                      know the printer inside it is a purchase. */}
                                  {r.fallback && (
                                    <span className="px-1 py-0.5 rounded text-[9px] font-bold uppercase bg-stone-200 text-stone-600">
                                      {FINANCE_MODE_LABELS[r.saleMode] || r.saleMode}
                                    </span>
                                  )}
                                  <span className="ml-auto flex items-baseline gap-1.5 tabular-nums">
                                    {r.monthlyCents > 0 ? (
                                      <>
                                        {r.listMonthlyCents > r.monthlyCents && (
                                          <span className="text-[11px] text-stone-400 line-through">{money(r.listMonthlyCents)}</span>
                                        )}
                                        <span className="font-semibold">{money(r.monthlyCents)}/mo</span>
                                      </>
                                    ) : r.oneTimeCents > 0 ? (
                                      <>
                                        {r.listOneTimeCents > r.oneTimeCents && (
                                          <span className="text-[11px] text-stone-400 line-through">{money(r.listOneTimeCents)}</span>
                                        )}
                                        <span className="font-semibold">{money(r.oneTimeCents)} once</span>
                                      </>
                                    ) : (
                                      <span className="font-semibold text-emerald-700">Included free</span>
                                    )}
                                  </span>
                                </div>
                              ))}

                              <div className="flex items-baseline gap-2 mt-2 pt-2 border-t border-stone-200">
                                <span className="text-[10px] uppercase tracking-[.08em] text-stone-500 font-bold">
                                  {/* Month one is what a customer is quoted, so
                                      that is what a scenario markets AS. The
                                      settled figure follows it, never replaces
                                      it. */}
                                  {sc.changes ? 'Marketed · first month' : 'Marketed monthly'}
                                </span>
                                <span className="ml-auto flex items-baseline gap-2">
                                  {sc.listMonthlyCents > (sc.changes ? sc.firstMonthlyCents : sc.monthlyCents) && (
                                    <span className="text-[11px] text-stone-400 line-through tabular-nums">
                                      {money(sc.listMonthlyCents)}
                                    </span>
                                  )}
                                  <span className="text-xl font-extrabold tabular-nums">
                                    {sc.changes
                                      ? (sc.firstMonthlyCents === 0 ? 'Free' : money(sc.firstMonthlyCents))
                                      : money(sc.monthlyCents)}
                                  </span>
                                </span>
                              </div>
                              {sc.changes ? (
                                <div className="mt-1.5 rounded-lg border border-rose-200 bg-rose-50/60 p-2">
                                  <p className="flex items-center gap-1.5 flex-wrap text-[11px] mb-1">
                                    <span className="px-1.5 py-0.5 rounded font-extrabold uppercase tracking-wide text-[9px] bg-rose-100 text-rose-700 border border-rose-200">
                                      Limited-time price
                                    </span>
                                    <span className="text-stone-600">
                                      settles at <strong className="tabular-nums">{money(sc.monthlyCents)}</strong>/mo
                                    </span>
                                  </p>
                                  {sc.phases.map((ph, i) => (
                                    <div key={i} className="flex items-baseline gap-2 py-0.5 text-[11px]">
                                      <span className="text-stone-600">
                                        {ph.iterations
                                          ? `Months ${ph.startMonth + 1}–${ph.startMonth + ph.iterations}`
                                          : `Month ${ph.startMonth + 1} onward`}
                                      </span>
                                      <span className="ml-auto font-bold tabular-nums text-stone-900">
                                        {ph.totalCents === 0 ? 'Free' : `${money(ph.totalCents)}/mo`}
                                      </span>
                                    </div>
                                  ))}
                                  {sc.disclosure && (
                                    <p className="mt-1.5 pt-1.5 border-t border-rose-200 text-[11px] text-stone-700">
                                      <span className="font-semibold">Customer is told:</span> &ldquo;{sc.disclosure}&rdquo;
                                    </p>
                                  )}
                                  <p className="text-[10px] text-stone-500 mt-1">
                                    settled: software {priceCents === null ? '—' : money(priceCents)} + devices{' '}
                                    {money(sc.deviceMonthlyCents)}
                                  </p>
                                </div>
                              ) : (
                                <p className="text-[10px] text-stone-500">
                                  software {priceCents === null ? '—' : money(priceCents)} + devices {money(sc.deviceMonthlyCents)}
                                </p>
                              )}

                              <div className="flex items-baseline gap-2 mt-1 text-[12px]">
                                <span className="text-stone-600">
                                  Due today
                                  {sc.dueTodayCents === 0 && (
                                    <span className="text-emerald-700 font-bold"> — $0 down</span>
                                  )}
                                </span>
                                <span className="ml-auto font-bold tabular-nums">{money(sc.visibleDueTodayCents)}</span>
                              </div>

                              {/* The operator always sees the FULL figure, even
                                  where the customer preview withholds a line. */}
                              {sc.hasHiddenDueToday && (
                                <p className="text-[10px] text-amber-800 mt-1 flex items-start gap-1">
                                  <AlertTriangle size={11} className="mt-0.5 shrink-0" />
                                  Customer sees {money(sc.visibleDueTodayCents)}; <strong className="tabular-nums">{money(sc.dueTodayCents)}</strong> is billed and invoiced.
                                </p>
                              )}
                            </div>
                          </React.Fragment>
                        ))}
                      </div>

                      {deviceDeal.uncounted.length > 0 && (
                        <p className="text-[11px] text-amber-700 mt-2">
                          {deviceDeal.uncounted.length} device offer(s) have no sale mode, so they are in no scenario:{' '}
                          {deviceDeal.uncounted.join(', ')}.
                        </p>
                      )}
                      <p className="text-[11px] text-stone-400 mt-2">
                        These are <strong>alternatives</strong>, not a running total — a customer takes one option per
                        device. A device offered in only one way appears in every scenario at that price. Only the
                        software figure is stored as the deal&rsquo;s price; each device prices through its own SKU and
                        the terms above.
                      </p>
                    </div>
                  );
                })()}
              </div>

              {/* SECTION 3c: HARDWARE PAPERWORK (2026-08-30) — directly under
                  Device Packages, the same way software paperwork sits under
                  the software price. */}
              <DealPaperworkSection
                section="hardware"
                title="Hardware paperwork"
                hint="What a tenant signs and accepts for the devices on this deal — finance, rental and damage terms."
                value={formData.documents}
                onChange={(documents) => setFormData({ ...formData, documents })}
                catalogue={documentCatalogue}
              />

              {/* SECTION 3d: DEAL-WIDE PAPERWORK (2026-08-30) — the refund
                  policy, which spans both halves, and operator-named lines. */}
              <DealDocumentExtras
                value={formData.documents}
                onChange={(documents) => setFormData({ ...formData, documents })}
                catalogue={documentCatalogue}
              />

              {/* The binder, flattened by the same resolver checkout uses —
                  the one place a retired or missing document shows up before
                  a customer runs into it. */}
              <DealDocumentPreview value={formData.documents} catalogue={documentCatalogue} />

              {/* SECTION 6: Provenance (Edit mode only) — TASK 4 polish: who
                  created this deal AND WHEN, plus who most recently edited it. */}
              {editingDeal && (
                <div className="p-4 bg-stone-50 rounded-xl border border-stone-200 text-stone-800">
                  <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Provenance</h3>
                  <p className="text-sm text-stone-700">
                    Created by <strong className="font-semibold text-stone-800">{editingDeal.createdBy ?? '—'}</strong>
                    {editingDeal.createdAt && (
                      <> on <strong className="font-semibold text-stone-800">{new Date(editingDeal.createdAt).toLocaleDateString()}</strong></>
                    )}
                  </p>
                  {editingDeal.updatedBy && (
                    <p className="text-sm text-stone-700 mt-1">
                      Last edited by <strong className="font-semibold text-stone-800">{editingDeal.updatedBy}</strong>
                      {editingDeal.updatedAt && (
                        <> on <strong className="font-semibold text-stone-800">{new Date(editingDeal.updatedAt).toLocaleDateString()}</strong></>
                      )}
                    </p>
                  )}
                  <div className="mt-3 space-y-2">
                    {(!editingDeal.pricingHistory || editingDeal.pricingHistory.length === 0) ? (
                      <p className="text-sm text-stone-400">—</p>
                    ) : (
                      [...editingDeal.pricingHistory].reverse().map((entry, i) => {
                        const strike = entry.listCentsAtChange != null && entry.priceCents != null
                          ? dealStrikethrough(entry.listCentsAtChange, entry.priceCents)
                          : null;
                        return (
                          <p key={i} className="text-sm text-stone-700">
                            <span className="font-medium tabular-nums">
                              {entry.priceCents != null ? money(entry.priceCents) : '—'}
                            </span>
                            {strike?.show && (
                              <span className="text-stone-500"> · was {money(entry.listCentsAtChange!)} · −{strike.percentOff}%</span>
                            )}
                            <span className="text-stone-500"> · by {entry.by}</span>
                            <span className="text-stone-400"> · {new Date(entry.at).toLocaleDateString()}</span>
                          </p>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Preview pane — sticky within the page's own scroll */}
            <div className="mt-6 lg:mt-0 lg:sticky lg:top-4">
              <DealLandingPreview model={previewModel} />
            </div>
          </div>
          )}
        </div>
      </div>

      {publishOpen && editingDeal && (
        <DealPublishModal
          changeType={publishChangeType}
          onChangeType={setPublishChangeType}
          affectedCount={versionsLoading || versionsError
            ? null
            : versionRows.filter((v: any) => v.status === 'published' && v.versionNo !== activeVersionNo).length}
          changes={publishChanges}
          effectiveAt={publishEffectiveAt}
          onEffectiveAtChange={setPublishEffectiveAt}
          busy={publishBusy}
          error={publishError}
          onConfirm={doPublish}
          onCancel={() => { setPublishOpen(false); setPublishError(null); }}
        />
      )}

      {/* Reads the SAVED deal, not `formData` — everything it shows a partner
          has to be what the stored record says, not what is on screen. */}
      {editingDeal && (
        <SendDealToPartnersModal
          open={showSendToPartners}
          onClose={() => setShowSendToPartners(false)}
          deal={editingDeal}
          packages={packages}
        />
      )}
    </div>
  );
};

export default DealEditPage;